import {
  t,
  type Locale,
  getLocale,
  RESOURCE_INFO,
  ResourceTier,
  ALL_RESOURCE_TIERS,
  sellPriceForResource,
  sellPriceForEquipment,
  lookupWeapon,
  lookupShield,
  lookupEngine,
  DEFAULT_WEAPON_ID,
  DEFAULT_SHIELD_ID,
  DEFAULT_ENGINE_ID,
  CONSUMABLES,
  SHIP_STATS,
  ShipClass,
  shipNameKey,
  formatShipTraitSummary,
  type StationShopSnapshotMsg,
  type SystemBountyContract,
  itemLabel,
  itemDescription,
  missionsCapFor,
  ZONE_TIER_KEYS,
  type WireMission,
  type PlayerShipCosmetics,
  type ShipPatternId,
  SHIP_PATTERN_PRESETS,
  DEFAULT_SHIP_COSMETICS,
  DEFAULT_SHIP_PATTERN,
  DEFAULT_SHIP_PRIMARY_COLOR,
  DEFAULT_SHIP_SECONDARY_COLOR,
  SHIP_CLASS_KEY,
  RACE_INFO,
  type RaceId,
  listCodexEntries,
  getCodexEntry,
} from '@viral-space/shared';
import { mountShipCardPreview, type ShipCardPreviewHandle } from './ship-card-preview';
import type { EquipmentState } from '../game/state.js';
import { attachMobileCloseButton } from './ui-shared';
import {
  renderWeaponThumbnail,
  renderShieldThumbnail,
  renderEngineThumbnail,
  renderConsumableThumbnail,
} from './item-thumbnail';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bg: 'rgba(0,16,0,0.6)',
  bgSolid: 'rgba(0,8,0,0.92)',
  dim: 'rgba(0,255,65,0.3)',
  panelBg: 'rgba(0,12,0,0.8)',
  /** Premium accent — gold matches the upgrade modal CTA. */
  gold: '#ffd700',
  goldDim: 'rgba(255,215,0,0.55)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px #00ff41, 0 0 12px rgba(0,255,65,0.3)`;
const BOX_GLOW = `0 0 8px rgba(0,255,65,0.4), inset 0 0 8px rgba(0,255,65,0.05)`;

type TabId = 'shop' | 'sell' | 'crafting' | 'missions' | 'hangar' | 'marketplace' | 'territory' | 'bounty' | 'codex';

export type CategoryKey = 'weapon' | 'shield' | 'engine' | 'consumable' | 'other';
type CategoryFilter = 'all' | CategoryKey;

export interface PlayerResourceSnapshot {
  resources: Map<ResourceTier, number>;
  credits: number;
}

interface ShopItem {
  itemId: string;
  name: string;
  price: number;
  requiredLevel?: number;
  stock?: number;
  description?: string;
  /** Localized display label, e.g. "WEAPON" / "ARMA". */
  category?: string;
  /** Stable, locale-independent category for filtering. */
  categoryKey?: CategoryKey;
  extraBadge?: string;
  /**
   * `true` when this row is gated to Premium subscribers — the BUY
   * button is replaced with an "UNLOCK PREMIUM" CTA for free pilots
   * and the card draws a gold badge so the player can see what
   * subscribing unlocks.
   */
  premiumOnly?: boolean;
}

interface CraftingRecipe {
  id: string;
  name: string;
  ingredients: { name: string; amount: number; have: number }[];
  craftTime: number;
  canCraft: boolean;
  description?: string;
  requiredLevel?: number;
  /** Localized display label, e.g. "WEAPON" / "ARMA". */
  category?: string;
  /** Stable, locale-independent category for filtering. */
  categoryKey?: CategoryKey;
  reason?: string;
}

interface MissionData {
  id: string;
  title: string;
  description: string;
  rewardXp: number;
  rewardCredits: number;
  requiredLevel: number;
  /** Already accepted by the local pilot — ACCEPT button is disabled. */
  isActive: boolean;
  /**
   * `true` when this row was injected from the player's active log
   * because the mission is NOT in the docked station's current
   * catalogue. Caused by docking at a different station or by a
   * daily/weekly id that has rolled over. Renders with an "ACCEPTED
   * ELSEWHERE" badge so the pilot can still see + abandon the
   * mission from the station Missions tab instead of being stuck at
   * the slot cap with an invisible row.
   */
  orphan?: boolean;
  /**
   * `true` when the mission is gated to Premium subscribers. Free
   * pilots still see the row (so they know what subscribing
   * unlocks) but the ACCEPT button is replaced with an "★ UNLOCK
   * PREMIUM" CTA that opens the upgrade modal. Optional / undefined
   * for the common case.
   */
  premiumOnly?: boolean;
}

export interface HangarData {
  /** Ship-class ids this pilot currently owns. Always includes `active`. */
  owned: number[];
  /** Ship-class id of the hull the pilot is currently flying. */
  active: number;
  /** Live credit balance — used to grey out BUY on hulls the pilot can't afford. */
  credits: number;
  /** Current pilot level — used to grey out BUY on hulls the pilot can't pilot yet. */
  level: number;
  /**
   * Pilot race id, used by the Customize modal to render the live
   * preview with the correct race hull color (hull is fixed per race
   * and not customizable — only the accent color and pattern are).
   * `null` when the local state hasn't received the player blob yet,
   * in which case the modal falls back to the neutral default hull.
   */
  raceId: RaceId | null;
  /**
   * Currently equipped per-pilot cosmetics (pattern + accent color).
   * Powers the Customize button's preview thumbnail and the modal's
   * initial picker state. Always defined — defaults to
   * {@link DEFAULT_SHIP_COSMETICS} when the pilot hasn't customised.
   */
  cosmetics: PlayerShipCosmetics;
}

/**
 * High-level state of the docked sector for the Territory tab. Drives
 * which "card" the renderer shows: a yours-with-perks panel, a
 * frontier-claimable pitch, an enemy-owned siege primer, etc.
 */
export type TerritoryStatus =
  | 'frontier'      // claimable — no dominant race, no current claim
  | 'lore-locked'   // NPC-dominant race; claims are forbidden by design
  | 'yours'         // current player owns this sector's claim
  | 'enemy'         // another player owns this sector's claim
  | 'cooldown';     // recently transferred — claims sealed for now

/**
 * Headline race info shown on the Territory tab's "race card". Pulled
 * from `RACE_INFO` + `formatRaceTraitSummary` on the main.ts side so the
 * UI doesn't need direct access to the constants table.
 */
export interface TerritoryRaceCard {
  raceId: number;
  name: string;
  description: string;
  disposition: 'hostile' | 'neutral' | 'friendly' | 'unknown';
  primaryColor: number;
  secondaryColor: number;
  traitLines: string[];
}

export interface TerritoryPanelData {
  sectorKey: string;
  /** Friendly procedural name, e.g. "Cygnus Drift Prime". */
  sectorName: string;
  /**
   * Open-space risk tier (1..9) — see `ZONE_TIER_KEYS` for the named
   * ladder. Stored as `number` because the ladder grew past the
   * original 1/2/3 range to accommodate apex deep-sector content.
   */
  difficultyTier: number;
  /** What kind of territory this is — drives the renderer. */
  status: TerritoryStatus;
  /** Race attached to the sector (dominant for NPC-locked, owner-race for player claims). `null` only on pure frontier. */
  race: TerritoryRaceCard | null;
  /** Display name of the player who owns the claim (null when unclaimed). */
  ownerName: string | null;
  /** True when `ownerName` is the local pilot. */
  ownerIsYou: boolean;
  /** Current garrison strength (0 when unclaimed), max possible level, and NPCs spawned per level. */
  garrisonLevel: number;
  garrisonLevelMax: number;
  garrisonNpcsPerLevel: number;
  /** Active siege state on this sector, or `null` when idle. */
  siegePhase: 'contested' | 'underSiege' | 'interrupted' | null;
  siegeEndsAt: number | null;
  siegeAttackerName: string | null;
  /** Epoch-ms after which the sector becomes claimable again, or `null`. */
  cooldownUntil: number | null;
  /** Total minutes an attacker must hold the dock before ownership transfers. */
  siegeWindowMinutes: number;
  /** Owner-only economy perks (display only — server is authoritative). */
  ownerShopDiscountPct: number;
  ownerRevenueSharePct: number;
  ownerRevenueDailyCap: number;

  /** Claim/reinforce affordances. */
  claimCost: number;
  reinforceCost: number | null;
  targetGarrisonLevel: number | null;
  canClaim: boolean;
  canReinforce: boolean;
  /** Optional human hint shown when neither action is available. */
  hint?: string;
  /** Player's holdings list (rendered at the bottom of the tab). */
  ownedSectors: Array<{
    sectorKey: string;
    label: string;
    statusLabel: string;
    raceColor?: number;
    garrisonLevel?: number;
    isCurrent?: boolean;
  }>;
}

export interface StationData {
  name: string;
  faction: string;
  shopItems: ShopItem[];
  recipes: CraftingRecipe[];
  missions: MissionData[];
  hangar: HangarData;
  /** When set, the Territory tab shows claim / reinforce actions. */
  territory?: TerritoryPanelData;
  /**
   * Surface id when the pilot has docked at a planetary base
   * (Phase 3.2). Drives the reputation meter in the header and the
   * Liberator badge. `undefined` for orbital stations.
   */
  surfaceId?: string;
}

const PAGE_SIZE = 12;
const CARD_MIN_WIDTH = 'clamp(220px, 22vw, 280px)';

/**
 * Compact "Xm Ys" formatter for bounty contract expiry countdowns.
 * Drops the seconds component once we have ≥10 minutes of headroom so
 * the row doesn't tick visibly while the player is glancing at the
 * board, and falls back to "<1m" once we slip under a minute so an
 * about-to-expire contract draws the eye.
 */
function formatBountyTimeRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes >= 10) return `${minutes}m`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

/**
 * Minimal mount contract the station UI needs from the marketplace
 * view. Lets `station-ui.ts` stay decoupled from `marketplace-panel.ts`
 * (and lets us swap implementations in tests) — anything that can be
 * appended into / removed from the tab content satisfies it.
 */
export interface MarketplaceMountable {
  mount(container: HTMLElement): void;
  unmount(): void;
}

export class StationUI {
  private container: HTMLElement;
  private root!: HTMLElement;
  private tabContent!: HTMLElement;
  private activeTab: TabId = 'shop';
  private tabButtons: Map<TabId, HTMLElement> = new Map();
  private stationData: StationData | null = null;

  private shopFilter: CategoryFilter = 'all';
  private shopPage = 0;
  private craftFilter: CategoryFilter = 'all';
  private craftPage = 0;

  onBuy: ((itemId: string, info: { name: string; price: number }) => void) | null = null;
  /**
   * Fired when a free pilot clicks the BUY button on a Premium-only
   * shop row. The parent (Game) opens the upgrade modal — the source
   * tag distinguishes shop-row clicks from pause-menu / character-
   * select clicks for analytics.
   */
  onUpgradeClick: ((source: 'shop') => void) | null = null;
  /** Second arg is the localized display name for craft-success HUD feedback. */
  onCraft: ((recipeId: string, displayName: string) => void) | null = null;
  onMissionAccept: ((missionId: string) => void) | null = null;
  /**
   * Drop an already-accepted mission directly from the station Missions
   * tab. Lets the player free a slot when the log is full without
   * having to leave the station UI / open the journal panel.
   */
  onMissionAbandon: ((missionId: string) => void) | null = null;
  onUndock: (() => void) | null = null;
  onSwitchShip: ((shipClass: number) => void) | null = null;
  onBuyShip: ((shipClass: number) => void) | null = null;
  /**
   * Apply a new pattern + color combination from the Hangar Customize
   * modal. The handler is responsible for routing the request to the
   * server (see `Network.sendSetShipCosmetics`); the modal closes on
   * apply and is re-opened to the resolved cosmetics if the server
   * clamps the request (free pilot tried a premium pattern).
   */
  onSetShipCosmetics: ((cosmetics: PlayerShipCosmetics) => void) | null = null;
  onClaimSector: ((sectorKey: string) => void) | null = null;
  onReinforceGarrison: ((sectorKey: string, targetLevel: number) => void) | null = null;
  onSell: ((itemId: string, quantity: number) => void) | null = null;
  /**
   * Inline marketplace view. Wired in `main.ts` from a `MarketplacePanel`
   * instance — the station tab simply mounts/unmounts it as the user
   * switches to/away from the MARKETPLACE tab so we don't need a second
   * modal layered on top of the docked UI.
   */
  marketplaceView: MarketplaceMountable | null = null;
  getPlayerResources: (() => PlayerResourceSnapshot) | null = null;
  getPlayerLevel: (() => number) | null = null;
  getEquipment: (() => EquipmentState) | null = null;
  /**
   * Live snapshot of the pilot's unlocked codex fragment ids. The
   * Codex tab uses this to render "discovered N of M" + the unlocked
   * entry list. Falls back to an empty set when not wired.
   */
  getUnlockedCodex: (() => ReadonlySet<string>) | null = null;
  /**
   * Resolve the pilot's reputation [-100, 100] for the supplied
   * surface id (Phase 3.2). Returns 0 when not wired or when the
   * pilot has never visited the planet.
   */
  getPlanetReputation: ((surfaceId: string) => number) | null = null;
  /**
   * Set of surface ids the pilot has liberated (Phase 3.3). Drives
   * the "Liberator of <Planet>" badge in the dock UI header.
   */
  getLiberatedSurfaces: (() => ReadonlySet<string>) | null = null;
  /**
   * Live-updating hangar snapshot. The UI calls this every time the Hangar
   * tab is (re)rendered so the owned-ship list, active-hull badge, credit
   * balance, and BUY affordability follow the latest server state without
   * needing to re-issue `show()`.
   */
  getHangar: (() => HangarData) | null = null;
  /**
   * Returns the set of mission ids the local pilot has already accepted.
   * The Missions tab uses this to grey out the ACCEPT button so the
   * player can't double-accept a mission they're already on.
   */
  getActiveMissionIds: (() => Set<string>) | null = null;
  /**
   * Returns the player's full active-mission log. Used by the Missions
   * tab to render "orphan" accepted missions — entries that are in the
   * pilot's active log but NOT in the docked station's current
   * catalogue (e.g. a daily that rolled over to a new id, or a
   * mission accepted at a different station). Without this the
   * Missions tab can show "5 / 5 slots used" while only displaying 4
   * ACCEPTED rows, leaving one mission silently invisible.
   */
  getActiveMissions: (() => WireMission[]) | null = null;
  /**
   * Live-updating premium subscription accessor. Drives the mission
   * "slots used" counter so a Premium subscriber sees `5 / 8` instead
   * of `5 / 5`. Defaults to "free" when not wired so legacy renders
   * never accidentally inflate the cap.
   */
  isPremium: (() => boolean) | null = null;
  /**
   * Live accessor for the inter-station bounty board snapshot. Wired
   * from `main.ts`; returns whatever the latest `BountyBoardData`
   * push left in `GameState`. The Bounty tab calls this on every
   * render so a refresh push lands within one tick.
   */
  getBountyContracts: (() => { contracts: SystemBountyContract[]; serverTime: number }) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.build();
  }

  private css(el: HTMLElement, styles: Partial<CSSStyleDeclaration>) {
    Object.assign(el.style, styles);
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    this.css(e, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build() {
    this.root = this.el('div', {
      position: 'absolute',
      inset: '0',
      display: 'none',
      flexDirection: 'column',
      fontFamily: FONT,
      color: COLORS.primary,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      zIndex: '50',
      background: COLORS.bgSolid,
      padding: 'clamp(8px, 2vw, 24px)',
      overflow: 'hidden',
      cursor: 'default',
      pointerEvents: 'auto',
    });
    this.container.appendChild(this.root);

    // Scanline overlay
    const scanline = this.el('div', {
      position: 'absolute',
      inset: '0',
      background:
        'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 4px)',
      pointerEvents: 'none',
      zIndex: '0',
    });
    this.root.appendChild(scanline);

    const inner = this.el('div', {
      position: 'relative',
      zIndex: '1',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      gap: '12px',
      overflow: 'hidden',
    });
    this.root.appendChild(inner);

    // Header row
    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: '0',
    });
    inner.appendChild(header);

    this.stationNameEl = this.el('div', {
      fontSize: 'clamp(16px, 2.5vw, 28px)',
      textShadow: GLOW,
      letterSpacing: '4px',
    }, 'STATION');
    header.appendChild(this.stationNameEl);

    // Right-side cluster: live credit balance + UNDOCK. Credits sit
    // here (not inside the SHOP tab body) so the player can read them
    // while browsing any tab — checking what they can afford against a
    // 200,000C cannon shouldn't require a tab swap to SELL just to see
    // the balance.
    const headerRight = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(10px, 1.5vw, 18px)',
    });
    header.appendChild(headerRight);

    this.headerCreditsEl = this.el('div', {
      fontSize: 'clamp(11px, 1.3vw, 14px)',
      color: COLORS.secondary,
      textShadow: GLOW,
      letterSpacing: '2px',
      whiteSpace: 'nowrap',
    }, '');
    headerRight.appendChild(this.headerCreditsEl);

    const undockBtn = this.el('button', {
      fontFamily: FONT,
      fontSize: 'clamp(12px, 1.5vw, 16px)',
      padding: '8px 24px',
      background: 'transparent',
      border: `2px solid ${COLORS.danger}`,
      color: COLORS.danger,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '3px',
      textShadow: `0 0 6px ${COLORS.danger}`,
      transition: 'all 0.2s',
      pointerEvents: 'auto',
    }, t('station.undock'));
    undockBtn.addEventListener('mouseenter', () => {
      undockBtn.style.background = COLORS.danger;
      undockBtn.style.color = '#000';
    });
    undockBtn.addEventListener('mouseleave', () => {
      undockBtn.style.background = 'transparent';
      undockBtn.style.color = COLORS.danger;
    });
    undockBtn.addEventListener('click', () => this.onUndock?.());
    headerRight.appendChild(undockBtn);

    attachMobileCloseButton(
      this.root,
      () => this.onUndock?.(),
      { hideOnMobile: [undockBtn] },
    );

    // Tabs
    const tabBar = this.el('div', {
      display: 'flex',
      gap: '2px',
      flexShrink: '0',
      overflowX: 'auto',
      pointerEvents: 'auto',
    });
    inner.appendChild(tabBar);

    const tabs: { id: TabId; labelKey: string }[] = [
      { id: 'shop', labelKey: 'station.shop' },
      { id: 'sell', labelKey: 'station.sell' },
      { id: 'crafting', labelKey: 'station.crafting' },
      { id: 'missions', labelKey: 'station.missions' },
      { id: 'bounty', labelKey: 'station.bounty' },
      { id: 'hangar', labelKey: 'station.hangar' },
      { id: 'territory', labelKey: 'territory.tab' },
      { id: 'marketplace', labelKey: 'station.marketplace' },
      { id: 'codex', labelKey: 'station.codex' },
    ];

    for (const tab of tabs) {
      const btn = this.el('button', {
        fontFamily: FONT,
        fontSize: 'clamp(11px, 1.3vw, 14px)',
        padding: '8px 18px',
        background: 'transparent',
        border: `1px solid ${COLORS.dim}`,
        borderBottom: 'none',
        color: COLORS.dim,
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        transition: 'all 0.15s',
        pointerEvents: 'auto',
        whiteSpace: 'nowrap',
      }, t(tab.labelKey));

      btn.addEventListener('click', () => this.switchTab(tab.id));
      tabBar.appendChild(btn);
      this.tabButtons.set(tab.id, btn);
    }

    // Content area
    this.tabContent = this.el('div', {
      flex: '1',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      boxShadow: BOX_GLOW,
      padding: 'clamp(8px, 1.5vw, 16px)',
      overflowY: 'auto',
      overflowX: 'hidden',
      pointerEvents: 'auto',
    });
    inner.appendChild(this.tabContent);

    this.switchTab('shop');
  }

  private stationNameEl!: HTMLElement;
  private headerCreditsEl!: HTMLElement;

  private updateHeaderCredits(): void {
    if (!this.headerCreditsEl) return;
    const credits = this.getPlayerResources?.().credits ?? 0;
    this.headerCreditsEl.textContent =
      `${t('hud.credits')}: ${credits.toLocaleString()}C`;
  }

  private switchTab(id: TabId) {
    this.activeTab = id;
    for (const [tabId, btn] of this.tabButtons) {
      if (tabId === id) {
        btn.style.background = COLORS.panelBg;
        btn.style.color = COLORS.primary;
        btn.style.borderColor = COLORS.dim;
        btn.style.textShadow = GLOW;
      } else {
        btn.style.background = 'transparent';
        btn.style.color = COLORS.dim;
        btn.style.borderColor = COLORS.dim;
        btn.style.textShadow = 'none';
      }
    }
    this.renderTab();
  }

  private renderTab() {
    // Detach the marketplace view first so the panel knows it has been
    // unmounted before we wipe `innerHTML`. Otherwise its internal
    // `mountedIn` reference would still point at the now-empty
    // container and `refresh()` calls from incoming server messages
    // would silently no-op against an orphaned root element.
    if (this.activeTab !== 'marketplace') {
      this.marketplaceView?.unmount();
    }
    this.tabContent.innerHTML = '';
    if (!this.stationData) {
      this.tabContent.appendChild(
        this.el('div', { color: COLORS.dim, padding: '16px' }, 'NO DATA'),
      );
      return;
    }

    switch (this.activeTab) {
      case 'shop':
        this.renderShop();
        break;
      case 'sell':
        this.renderSell();
        break;
      case 'crafting':
        this.renderCrafting();
        break;
      case 'missions':
        this.renderMissions();
        break;
      case 'bounty':
        this.renderBountyBoard();
        break;
      case 'hangar':
        this.renderHangar();
        break;
      case 'territory':
        this.renderTerritory();
        break;
      case 'marketplace':
        this.renderMarketplace();
        break;
      case 'codex':
        this.renderCodex();
        break;
    }
  }

  refresh() {
    if (this.root.style.display === 'none') return;
    this.updateHeaderCredits();
    this.renderTab();
  }

  private makeActionBtn(text: string, onClick: () => void, color: string = COLORS.primary): HTMLElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: '12px',
      padding: '6px 14px',
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      transition: 'all 0.15s',
      pointerEvents: 'auto',
      flexShrink: '0',
    }, text);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = color;
      btn.style.color = '#000';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = color;
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  /**
   * Snap visual feedback played the instant the player clicks BUY:
   *   - Pulse the entire shop card with a green flash + scale bump so
   *     the click is impossible to miss even at a glance.
   *   - Swap the BUY button into a "PURCHASING…" state so the user
   *     stops mashing the button while the server roundtrip resolves.
   *
   * Both transitions live inline (no CSS keyframes file) and tear down
   * on their own; the imminent {@link refresh} triggered by the server's
   * `InventoryUpdate` will replace the DOM nodes anyway.
   */
  private flashPurchaseFeedback(card: HTMLElement, btn: HTMLElement): void {
    const prevBg = card.style.background;
    const prevTransition = card.style.transition;
    const prevTransform = card.style.transform;
    const prevShadow = card.style.boxShadow;
    card.style.transition = 'background 0.18s, transform 0.18s, box-shadow 0.18s';
    card.style.background = 'rgba(0,255,65,0.22)';
    card.style.transform = 'scale(1.025)';
    card.style.boxShadow = '0 0 16px rgba(0,255,65,0.55), inset 0 0 12px rgba(0,255,65,0.35)';
    window.setTimeout(() => {
      card.style.background = prevBg;
      card.style.transform = prevTransform;
      card.style.boxShadow = prevShadow;
      card.style.transition = prevTransition;
    }, 180);

    const prevLabel = btn.textContent;
    const prevBtnBg = btn.style.background;
    const prevBtnColor = btn.style.color;
    btn.textContent = 'PURCHASING...';
    btn.style.background = COLORS.primary;
    btn.style.color = '#000';
    btn.style.pointerEvents = 'none';
    window.setTimeout(() => {
      // refresh() may have already replaced this node — check before
      // touching it so we don't reset the new button's state.
      if (!btn.isConnected) return;
      btn.textContent = prevLabel;
      btn.style.background = prevBtnBg;
      btn.style.color = prevBtnColor;
      btn.style.pointerEvents = 'auto';
    }, 600);
  }

  /* ───── Category helpers ───── */

  private categoryKeyOf(item: { categoryKey?: CategoryKey; category?: string }): CategoryKey {
    if (item.categoryKey) return item.categoryKey;
    const c = (item.category ?? '').toUpperCase();
    if (c.includes('WEAPON') || c.includes('ARMA')) return 'weapon';
    if (c.includes('SHIELD') || c.includes('ESCUDO')) return 'shield';
    if (c.includes('ENGINE') || c.includes('MOTOR')) return 'engine';
    if (c.includes('CONSUM')) return 'consumable';
    return 'other';
  }

  private categoryLabel(key: CategoryKey): string {
    switch (key) {
      case 'weapon': return t('station.catWeapon');
      case 'shield': return t('station.catShield');
      case 'engine': return t('station.catEngine');
      case 'consumable': return t('station.catConsumable');
      default: return '';
    }
  }

  /** 18×18 inline SVG icon for a category; uses currentColor so it picks
   *  up the surrounding text color (green glow, danger red, etc.). */
  private categoryIconHtml(key: CategoryKey): string {
    const base = 'width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
    switch (key) {
      case 'weapon':
        // Targeting reticle: circle + 4 ticks + center dot.
        return `<svg ${base}>
          <circle cx="12" cy="12" r="7"/>
          <line x1="12" y1="2" x2="12" y2="6"/>
          <line x1="12" y1="18" x2="12" y2="22"/>
          <line x1="2" y1="12" x2="6" y2="12"/>
          <line x1="18" y1="12" x2="22" y2="12"/>
          <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
        </svg>`;
      case 'shield':
        return `<svg ${base}>
          <path d="M12 3 L20 6 L20 12 C20 17 16 20 12 22 C8 20 4 17 4 12 L4 6 Z"/>
          <path d="M9 12 L11 14 L15 10" stroke-width="1.6"/>
        </svg>`;
      case 'consumable':
        // Flask-with-plus vibe.
        return `<svg ${base}>
          <circle cx="12" cy="12" r="8"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
        </svg>`;
      case 'engine':
        // Stylized thruster nozzle with exhaust arrows.
        return `<svg ${base}>
          <path d="M6 8 L14 8 L16 12 L14 16 L6 16 Z"/>
          <line x1="16" y1="12" x2="21" y2="12"/>
          <line x1="18" y1="9" x2="21" y2="9"/>
          <line x1="18" y1="15" x2="21" y2="15"/>
        </svg>`;
      default:
        return `<svg ${base}>
          <rect x="4" y="4" width="16" height="16"/>
          <line x1="4" y1="12" x2="20" y2="12"/>
          <line x1="12" y1="4" x2="12" y2="20"/>
        </svg>`;
    }
  }

  private makeIconBox(key: CategoryKey, color: string): HTMLElement {
    const wrap = this.el('div', {
      width: '32px',
      height: '32px',
      border: `1px solid ${color}`,
      background: 'rgba(0,255,65,0.06)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: '0',
      color,
      boxShadow: `inset 0 0 6px rgba(0,255,65,0.12)`,
    });
    wrap.innerHTML = this.categoryIconHtml(key);
    return wrap;
  }

  /**
   * Like {@link makeIconBox}, but renders a 3D thumbnail of the actual
   * item (weapon, shield, engine, consumable) using the shared
   * offscreen renderer. The render is deferred to the next frame so a
   * shop page with 12 cards doesn't stall the main thread on first
   * paint.
   */
  private makeItemThumbBox(
    itemId: string | undefined,
    key: CategoryKey,
    borderColor: string,
  ): HTMLElement {
    const wrap = this.el('div', {
      width: '40px',
      height: '40px',
      border: `1px solid ${borderColor}`,
      background: 'rgba(0,255,65,0.06)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: '0',
      color: borderColor,
      boxShadow: `inset 0 0 6px rgba(0,255,65,0.12)`,
    });
    if (!itemId || (key !== 'weapon' && key !== 'shield' && key !== 'engine' && key !== 'consumable')) {
      wrap.innerHTML = this.categoryIconHtml(key);
      return wrap;
    }
    const cv = document.createElement('canvas');
    cv.width = 36;
    cv.height = 36;
    cv.style.width = '36px';
    cv.style.height = '36px';
    cv.style.display = 'block';
    wrap.appendChild(cv);
    requestAnimationFrame(() => {
      switch (key) {
        case 'weapon': renderWeaponThumbnail(cv, itemId); break;
        case 'shield': renderShieldThumbnail(cv, itemId); break;
        case 'engine': renderEngineThumbnail(cv, itemId); break;
        case 'consumable': renderConsumableThumbnail(cv, itemId); break;
      }
    });
    return wrap;
  }

  /** Builds a row of filter chips. `counts` keys: 'all' + every CategoryKey. */
  private buildFilterBar(
    active: CategoryFilter,
    counts: Record<CategoryFilter, number>,
    onSelect: (key: CategoryFilter) => void,
  ): HTMLElement {
    const bar = this.el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginBottom: '10px',
      pointerEvents: 'auto',
    });

    const entries: { key: CategoryFilter; label: string }[] = [
      { key: 'all', label: t('station.filterAll') },
      { key: 'weapon', label: t('station.filterWeapons') },
      { key: 'shield', label: t('station.filterShields') },
      { key: 'engine', label: t('station.filterEngines') },
      { key: 'consumable', label: t('station.filterConsumables') },
    ];

    for (const { key, label } of entries) {
      const count = counts[key] ?? 0;
      const isActive = active === key;
      const chip = this.el('button', {
        fontFamily: FONT,
        fontSize: '12px',
        padding: '6px 12px',
        background: isActive ? COLORS.primary : 'transparent',
        color: isActive ? '#000' : COLORS.primary,
        border: `1px solid ${isActive ? COLORS.primary : COLORS.dim}`,
        cursor: count === 0 && key !== 'all' ? 'not-allowed' : 'pointer',
        opacity: count === 0 && key !== 'all' ? '0.4' : '1',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        transition: 'all 0.15s',
        pointerEvents: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        textShadow: isActive ? 'none' : GLOW,
      });

      if (key !== 'all') {
        const icon = this.el('span', {
          display: 'inline-flex',
          alignItems: 'center',
          color: isActive ? '#000' : COLORS.primary,
        });
        icon.innerHTML = this.categoryIconHtml(key as CategoryKey);
        (icon.firstChild as SVGElement | null)?.setAttribute('width', '14');
        (icon.firstChild as SVGElement | null)?.setAttribute('height', '14');
        chip.appendChild(icon);
      }

      chip.appendChild(document.createTextNode(`${label} (${count})`));

      if (count > 0 || key === 'all') {
        chip.addEventListener('click', () => onSelect(key));
        chip.addEventListener('mouseenter', () => {
          if (!isActive) chip.style.borderColor = COLORS.primary;
        });
        chip.addEventListener('mouseleave', () => {
          if (!isActive) chip.style.borderColor = COLORS.dim;
        });
      }

      bar.appendChild(chip);
    }

    return bar;
  }

  /** Prev/next pager; returns null if only one page. */
  private buildPager(
    currentPage: number,
    totalPages: number,
    onChange: (newPage: number) => void,
  ): HTMLElement | null {
    if (totalPages <= 1) return null;

    const bar = this.el('div', {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      marginTop: '12px',
      fontSize: '12px',
      color: COLORS.dim,
      letterSpacing: '2px',
      pointerEvents: 'auto',
    });

    const prev = this.makeActionBtn(
      `◀ ${t('station.prevPage')}`,
      () => { if (currentPage > 0) onChange(currentPage - 1); },
      currentPage > 0 ? COLORS.primary : COLORS.dim,
    );
    if (currentPage === 0) {
      prev.style.cursor = 'not-allowed';
      prev.style.opacity = '0.4';
    }
    bar.appendChild(prev);

    bar.appendChild(
      this.el('span', {
        color: COLORS.primary,
        textShadow: GLOW,
        minWidth: '80px',
        textAlign: 'center',
      }, `${t('station.page')} ${currentPage + 1} / ${totalPages}`),
    );

    const next = this.makeActionBtn(
      `${t('station.nextPage')} ▶`,
      () => { if (currentPage < totalPages - 1) onChange(currentPage + 1); },
      currentPage < totalPages - 1 ? COLORS.primary : COLORS.dim,
    );
    if (currentPage >= totalPages - 1) {
      next.style.cursor = 'not-allowed';
      next.style.opacity = '0.4';
    }
    bar.appendChild(next);

    return bar;
  }

  private countByCategory<T extends { categoryKey?: CategoryKey; category?: string }>(
    items: T[],
  ): Record<CategoryFilter, number> {
    const counts: Record<CategoryFilter, number> = {
      all: items.length,
      weapon: 0,
      shield: 0,
      engine: 0,
      consumable: 0,
      other: 0,
    };
    for (const it of items) {
      counts[this.categoryKeyOf(it)] += 1;
    }
    return counts;
  }

  /* ───── Shop ───── */
  private renderShop() {
    const all = this.stationData!.shopItems;
    const counts = this.countByCategory(all);

    const filtered = this.shopFilter === 'all'
      ? all
      : all.filter((it) => this.categoryKeyOf(it) === this.shopFilter);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (this.shopPage >= totalPages) this.shopPage = totalPages - 1;
    if (this.shopPage < 0) this.shopPage = 0;
    const pageItems = filtered.slice(
      this.shopPage * PAGE_SIZE,
      (this.shopPage + 1) * PAGE_SIZE,
    );

    const wrapper = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    });

    wrapper.appendChild(
      this.buildFilterBar(this.shopFilter, counts, (key) => {
        this.shopFilter = key;
        this.shopPage = 0;
        this.renderTab();
      }),
    );

    if (pageItems.length === 0) {
      wrapper.appendChild(
        this.el('div', {
          color: COLORS.dim,
          padding: '32px',
          textAlign: 'center',
          fontSize: '13px',
        }, t('station.noItemsInCategory')),
      );
      this.tabContent.appendChild(wrapper);
      return;
    }

    const grid = this.el('div', {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}, 1fr))`,
      gap: '10px',
    });

    const playerLevel = this.getPlayerLevel?.() ?? 1;
    const credits = this.getPlayerResources?.().credits ?? 0;
    const eq = this.getEquipment?.() ?? null;
    // Engines are permanent — once a pilot owns one, the server refuses
    // a second purchase (`already_owned`). Surface that on the card up
    // front so the player can see what they have without burning a click
    // and a credit toast.
    const ownedEngineIds = new Set<string>(
      (eq?.ownedEngines ?? []).map((e) => e.id),
    );

    const isPremium = this.isPremium?.() ?? false;
    for (const item of pageItems) {
      const reqLevel = item.requiredLevel ?? 1;
      const levelOk = playerLevel >= reqLevel;
      const creditsOk = credits >= item.price;
      const catKey = this.categoryKeyOf(item);
      const alreadyOwned = catKey === 'engine' && ownedEngineIds.has(item.itemId);
      // Premium-only rows are visible to free pilots so they can see
      // what subscribing unlocks, but BUY is short-circuited into an
      // upgrade CTA. Premium subscribers see the standard BUY button.
      const premiumLocked = !!item.premiumOnly && !isPremium;
      const canBuy =
        levelOk && creditsOk && item.price > 0 && !alreadyOwned && !premiumLocked;
      const borderColor = premiumLocked
        ? COLORS.gold
        : !levelOk
          ? COLORS.danger
          : alreadyOwned
            ? COLORS.dim
            : COLORS.dim;

      const card = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px 14px',
        border: `1px solid ${borderColor}`,
        background: 'rgba(0,8,0,0.55)',
        opacity: !levelOk || alreadyOwned ? '0.7' : '1',
      });

      const head = this.el('div', {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      });
      head.appendChild(this.makeItemThumbBox(item.itemId, catKey, levelOk ? COLORS.primary : COLORS.danger));

      const titleBlock = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: '1',
        minWidth: '0',
      });
      titleBlock.appendChild(
        this.el('div', {
          fontSize: '14px',
          textShadow: GLOW,
          lineHeight: '1.15',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }, item.name),
      );
      titleBlock.appendChild(
        this.el('div', {
          fontSize: '10px',
          color: COLORS.dim,
          letterSpacing: '2px',
        }, item.category ?? this.categoryLabel(catKey)),
      );
      head.appendChild(titleBlock);
      card.appendChild(head);

      if (item.description) {
        card.appendChild(
          this.el('div', {
            fontSize: '11px',
            color: COLORS.dim,
            textTransform: 'none',
            lineHeight: '1.35',
            minHeight: '30px',
          }, item.description),
        );
      }

      const infoRow = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px',
      });
      infoRow.appendChild(
        this.el('span', {
          color: COLORS.secondary,
          textShadow: `0 0 4px ${COLORS.secondary}`,
        }, `${item.price.toLocaleString()}C`),
      );
      infoRow.appendChild(
        this.el('span', {
          color: levelOk ? COLORS.dim : COLORS.danger,
        }, `LVL ${reqLevel}+`),
      );
      card.appendChild(infoRow);

      if (item.extraBadge) {
        card.appendChild(
          this.el('div', {
            fontSize: '11px',
            color: COLORS.primary,
          }, item.extraBadge),
        );
      }

      const btnLabel = premiumLocked
        ? t('station.buyPremiumLocked')
        : !levelOk
          ? `LOCKED LVL ${reqLevel}`
          : alreadyOwned
            ? t('station.buyOwned')
            : !creditsOk
              ? 'INSUFFICIENT'
              : 'BUY';
      const btnColor = premiumLocked
        ? COLORS.gold
        : canBuy
          ? COLORS.primary
          : alreadyOwned
            ? COLORS.dim
            : levelOk
              ? COLORS.secondary
              : COLORS.danger;
      const btn = this.makeActionBtn(btnLabel, () => {
        if (premiumLocked) {
          // Hand off to the parent (Game) so the lazy upgrade modal
          // handles the actual checkout flow. Tag the source so
          // analytics / debugging can tell which surface drove the
          // upgrade click.
          this.onUpgradeClick?.('shop');
          return;
        }
        if (!canBuy) return;
        // Optimistic click feedback: flash the card + briefly swap the
        // button into a "buying" state. The InventoryUpdate (or Error)
        // that follows will rebuild the shop tab via refresh(), so this
        // animation is intentionally short-lived and we don't bother
        // restoring it manually.
        this.flashPurchaseFeedback(card, btn);
        this.onBuy?.(item.itemId, { name: item.name, price: item.price });
      }, btnColor);
      if (!canBuy && !premiumLocked) {
        btn.style.cursor = 'not-allowed';
        btn.style.opacity = '0.55';
      }
      if (premiumLocked) {
        card.style.boxShadow = '0 0 8px rgba(255,215,0,0.25), inset 0 0 8px rgba(255,215,0,0.05)';
      }
      card.appendChild(btn);

      grid.appendChild(card);
    }

    wrapper.appendChild(grid);

    const pager = this.buildPager(this.shopPage, totalPages, (p) => {
      this.shopPage = p;
      this.renderTab();
    });
    if (pager) wrapper.appendChild(pager);

    this.tabContent.appendChild(wrapper);
  }

  /* ───── Sell ───── */
  private renderSell() {
    const wrapper = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    });

    // The live credit balance now lives in the station-screen header
    // (right next to UNDOCK) so it's visible across every tab — keeping
    // a duplicate here would just be noise.
    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '11px',
      color: COLORS.dim,
    });
    header.appendChild(this.el('span', {}, t('station.sellHint')));
    wrapper.appendChild(header);

    const snapshot = this.getPlayerResources?.() ?? null;

    // ─── Resources section ───
    wrapper.appendChild(this.el('div', {
      fontSize: '11px',
      color: COLORS.secondary,
      letterSpacing: '2px',
      marginTop: '4px',
    }, t('station.sellResourcesHeading')));

    const tiers = ALL_RESOURCE_TIERS;

    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });

    let anyResource = false;

    for (const tier of tiers) {
      const qty = snapshot?.resources.get(tier) ?? 0;
      if (qty <= 0) continue;
      anyResource = true;

      const info = RESOURCE_INFO[tier];
      const c = `#${info.color.toString(16).padStart(6, '0')}`;
      const unitPrice = sellPriceForResource(tier, 1);

      const row = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 10px',
        border: `1px solid ${COLORS.dim}`,
        background: 'rgba(0,8,0,0.5)',
        gap: '10px',
        flexWrap: 'wrap',
      });

      const left = this.el('div', {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flex: '1',
        minWidth: '180px',
      });
      const dot = this.el('span', {
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: c,
        boxShadow: `0 0 6px ${c}`,
        flexShrink: '0',
      });
      left.appendChild(dot);

      const infoBlock = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      });
      infoBlock.appendChild(
        this.el('div', { fontSize: '12px', textShadow: GLOW }, info.name),
      );
      infoBlock.appendChild(
        this.el('div', { fontSize: '9px', color: COLORS.dim },
          `${qty}x  @ ${unitPrice}C / unit`),
      );
      left.appendChild(infoBlock);
      row.appendChild(left);

      const actions = this.el('div', {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        flexWrap: 'wrap',
      });

      actions.appendChild(
        this.makeActionBtn(t('station.sellOne'), () => {
          this.onSell?.(`resource_${tier}`, 1);
        }),
      );

      actions.appendChild(
        this.makeActionBtn(`${t('station.sellAll')} (+${(unitPrice * qty).toLocaleString()}C)`, () => {
          this.onSell?.(`resource_${tier}`, qty);
        }, COLORS.secondary),
      );

      row.appendChild(actions);
      list.appendChild(row);
    }

    if (!anyResource) {
      list.appendChild(
        this.el('div', {
          color: COLORS.dim,
          padding: '16px',
          textAlign: 'center',
        }, t('station.noResources')),
      );
    }

    wrapper.appendChild(list);

    // ─── Equipment (unused weapons / shields) ───
    wrapper.appendChild(this.el('div', {
      fontSize: '11px',
      color: COLORS.secondary,
      letterSpacing: '2px',
      marginTop: '12px',
    }, t('station.sellEquipmentHeading')));
    wrapper.appendChild(this.el('div', {
      fontSize: '10px',
      color: COLORS.dim,
      lineHeight: '1.4',
    }, t('station.sellEquipmentHint')));

    const equipList = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      marginTop: '6px',
    });

    const eq = this.getEquipment?.() ?? null;
    let equipEntries = 0;

    if (eq) {
      for (const owned of eq.ownedWeapons) {
        if (owned.id === DEFAULT_WEAPON_ID) continue;
        const def = lookupWeapon(owned.id);
        if (!def || def.creditCost <= 0) continue;
        const sellPrice = sellPriceForEquipment(def.creditCost);
        if (sellPrice <= 0) continue;

        const isEquipped = owned.id === eq.equippedWeaponId;
        // "Unused" means exactly one freshly-purchased pack. Unlimited
        // weapons (-1) and stacked packs (> maxAmmo) aren't sellable; this
        // mirrors the server's `sellEquipment` check so the UI never offers
        // a SELL button that would be rejected.
        const isFullPack =
          def.maxAmmo > 0 && owned.ammo === def.maxAmmo;

        equipList.appendChild(
          this.buildEquipmentSellRow({
            itemId: owned.id,
            name: def.name,
            categoryKey: 'weapon',
            sellPrice,
            isEquipped,
            isSellable: !isEquipped && isFullPack,
            unavailableReason: isEquipped
              ? t('station.sellEquipped')
              : !isFullPack ? t('station.sellUsed') : '',
          }),
        );
        equipEntries++;
      }

      for (const owned of eq.ownedShields) {
        if (owned.id === DEFAULT_SHIELD_ID) continue;
        const def = lookupShield(owned.id);
        if (!def || def.creditCost <= 0) continue;
        const sellPrice = sellPriceForEquipment(def.creditCost);
        if (sellPrice <= 0) continue;

        const isEquipped = owned.id === eq.equippedShieldId;
        const isFullPack =
          def.maxCharges > 0 && owned.charges === def.maxCharges;

        equipList.appendChild(
          this.buildEquipmentSellRow({
            itemId: owned.id,
            name: def.name,
            categoryKey: 'shield',
            sellPrice,
            isEquipped,
            isSellable: !isEquipped && isFullPack,
            unavailableReason: isEquipped
              ? t('station.sellEquipped')
              : !isFullPack ? t('station.sellUsed') : '',
          }),
        );
        equipEntries++;
      }

      for (const owned of eq.ownedEngines ?? []) {
        if (owned.id === DEFAULT_ENGINE_ID) continue;
        const def = lookupEngine(owned.id);
        if (!def || def.creditCost <= 0) continue;
        const sellPrice = sellPriceForEquipment(def.creditCost);
        if (sellPrice <= 0) continue;

        const isEquipped = owned.id === eq.equippedEngineId;
        // Engines don't deplete, so "used" is never a reason to refuse
        // the sale — we only guard against selling the equipped or
        // default engine (both enforced server-side too).
        equipList.appendChild(
          this.buildEquipmentSellRow({
            itemId: owned.id,
            name: def.name,
            categoryKey: 'engine',
            sellPrice,
            isEquipped,
            isSellable: !isEquipped,
            unavailableReason: isEquipped ? t('station.sellEquipped') : '',
          }),
        );
        equipEntries++;
      }
    }

    if (equipEntries === 0) {
      equipList.appendChild(
        this.el('div', {
          color: COLORS.dim,
          padding: '16px',
          textAlign: 'center',
        }, t('station.noEquipment')),
      );
    }

    wrapper.appendChild(equipList);
    this.tabContent.appendChild(wrapper);
  }

  /**
   * Row renderer used by the Sell tab for weapons and shields. Kept
   * separate so we can reuse the layout while the ownership/availability
   * semantics (equipped, partially used, stacked) differ per slot.
   */
  private buildEquipmentSellRow(opts: {
    itemId: string;
    name: string;
    categoryKey: CategoryKey;
    sellPrice: number;
    isEquipped: boolean;
    isSellable: boolean;
    unavailableReason: string;
  }): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 10px',
      border: `1px solid ${opts.isSellable ? COLORS.dim : 'rgba(255,68,68,0.35)'}`,
      background: 'rgba(0,8,0,0.5)',
      gap: '10px',
      flexWrap: 'wrap',
    });

    const left = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: '1',
      minWidth: '180px',
    });
    left.appendChild(this.makeItemThumbBox(opts.itemId, opts.categoryKey, opts.isSellable ? COLORS.primary : COLORS.danger));
    const infoBlock = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    });
    infoBlock.appendChild(this.el('div', { fontSize: '12px', textShadow: GLOW }, opts.name));
    infoBlock.appendChild(
      this.el('div', { fontSize: '9px', color: COLORS.dim },
        opts.isSellable
          ? `+${opts.sellPrice.toLocaleString()}C`
          : opts.unavailableReason),
    );
    left.appendChild(infoBlock);
    row.appendChild(left);

    if (opts.isSellable) {
      row.appendChild(
        this.makeActionBtn(`${t('station.sellBtn')} (+${opts.sellPrice.toLocaleString()}C)`, () => {
          this.onSell?.(opts.itemId, 1);
        }, COLORS.secondary),
      );
    } else {
      const disabled = this.makeActionBtn(t('station.sellBtn'), () => {}, COLORS.danger);
      disabled.style.cursor = 'not-allowed';
      disabled.style.opacity = '0.45';
      row.appendChild(disabled);
    }

    return row;
  }

  /* ───── Crafting ───── */
  private renderCrafting() {
    const all = this.stationData!.recipes;
    const counts = this.countByCategory(all);

    const filtered = this.craftFilter === 'all'
      ? all
      : all.filter((r) => this.categoryKeyOf(r) === this.craftFilter);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (this.craftPage >= totalPages) this.craftPage = totalPages - 1;
    if (this.craftPage < 0) this.craftPage = 0;
    const pageItems = filtered.slice(
      this.craftPage * PAGE_SIZE,
      (this.craftPage + 1) * PAGE_SIZE,
    );

    const wrapper = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    });

    wrapper.appendChild(
      this.buildFilterBar(this.craftFilter, counts, (key) => {
        this.craftFilter = key;
        this.craftPage = 0;
        this.renderTab();
      }),
    );

    if (pageItems.length === 0) {
      wrapper.appendChild(
        this.el('div', {
          color: COLORS.dim,
          padding: '32px',
          textAlign: 'center',
          fontSize: '13px',
        }, t('station.noItemsInCategory')),
      );
      this.tabContent.appendChild(wrapper);
      return;
    }

    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    });

    for (const recipe of pageItems) {
      const catKey = this.categoryKeyOf(recipe);
      const borderColor = recipe.canCraft ? COLORS.dim : COLORS.danger;

      const row = this.el('div', {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 14px',
        border: `1px solid ${borderColor}`,
        background: 'rgba(0,8,0,0.55)',
        gap: '14px',
        flexWrap: 'wrap',
      });

      row.appendChild(
        this.makeItemThumbBox(recipe.id, catKey, recipe.canCraft ? COLORS.primary : COLORS.danger),
      );

      const info = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        flex: '1',
        minWidth: '220px',
      });

      const head = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        alignItems: 'baseline',
      });
      head.appendChild(
        this.el('div', { fontSize: '14px', textShadow: GLOW }, recipe.name),
      );
      head.appendChild(
        this.el('div', {
          fontSize: '10px',
          color: COLORS.dim,
          letterSpacing: '2px',
        }, recipe.category ?? this.categoryLabel(catKey)),
      );
      info.appendChild(head);

      if (recipe.description) {
        info.appendChild(
          this.el('div', {
            fontSize: '11px',
            color: COLORS.dim,
            textTransform: 'none',
            lineHeight: '1.35',
          }, recipe.description),
        );
      }

      const ingredients = this.el('div', {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: COLORS.dim,
      });
      for (const ing of recipe.ingredients) {
        const c = ing.have >= ing.amount ? COLORS.primary : COLORS.danger;
        ingredients.appendChild(
          this.el('span', { color: c }, `${ing.name}: ${ing.have}/${ing.amount}`),
        );
      }
      if (recipe.requiredLevel && recipe.requiredLevel > 1) {
        ingredients.appendChild(
          this.el('span', {
            color: COLORS.secondary,
          }, `LVL ${recipe.requiredLevel}+`),
        );
      }
      info.appendChild(ingredients);
      row.appendChild(info);

      const craftColor = recipe.canCraft ? COLORS.primary : COLORS.danger;
      const btnLabel = recipe.canCraft ? 'CRAFT' : (recipe.reason ?? 'LOCKED');
      const btn = this.makeActionBtn(btnLabel, () => {
        if (recipe.canCraft) this.onCraft?.(recipe.id, recipe.name);
      }, craftColor);
      if (!recipe.canCraft) {
        btn.style.cursor = 'not-allowed';
        btn.style.opacity = '0.5';
      }
      row.appendChild(btn);

      list.appendChild(row);
    }

    wrapper.appendChild(list);

    const pager = this.buildPager(this.craftPage, totalPages, (p) => {
      this.craftPage = p;
      this.renderTab();
    });
    if (pager) wrapper.appendChild(pager);

    this.tabContent.appendChild(wrapper);
  }

  /* ───── Missions ───── */
  private renderMissions() {
    const wrapper = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    });

    const activeIds = this.getActiveMissionIds?.() ?? new Set<string>();
    const playerLevel = this.getPlayerLevel?.() ?? 1;
    const slotsUsed = activeIds.size;
    const cap = missionsCapFor(this.isPremium?.() ?? false);
    const atCap = slotsUsed >= cap;

    // Header strip — capacity counter + a hint when full so the player
    // knows up front why some ACCEPT buttons are inert.
    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 10px',
      border: `1px solid ${atCap ? COLORS.danger : COLORS.dim}`,
      background: atCap ? 'rgba(40,0,0,0.4)' : 'rgba(0,16,0,0.4)',
      fontSize: '10px',
      flexWrap: 'wrap',
    });
    header.appendChild(this.el('span', { color: COLORS.dim },
      t('mission.slotsUsed', { used: slotsUsed, max: cap })));
    if (atCap) {
      header.appendChild(this.el('span', { color: COLORS.danger, textShadow: GLOW },
        t('mission.logFullHint')));
    }
    wrapper.appendChild(header);

    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    });

    const isPremium = this.isPremium?.() ?? false;
    for (const mission of this.stationData!.missions) {
      const isAccepted = mission.isActive || activeIds.has(mission.id);
      const levelOk = playerLevel >= mission.requiredLevel;
      const premiumGated = !!mission.premiumOnly && !isPremium && !isAccepted;
      // Premium contracts get a gold trim so they jump out of the
      // catalogue. Already-accepted rows keep the primary green
      // border so the pilot can still spot them in the list.
      const borderColor = isAccepted
        ? COLORS.primary
        : mission.premiumOnly
          ? COLORS.gold
          : (levelOk ? COLORS.dim : COLORS.danger);
      const row = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 10px',
        border: `1px solid ${borderColor}`,
        background: isAccepted
          ? 'rgba(0,32,0,0.45)'
          : mission.premiumOnly && !isAccepted
            ? 'rgba(32,24,0,0.5)'
            : 'rgba(0,8,0,0.5)',
        gap: '8px',
        flexWrap: 'wrap',
      });

      const info = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        flex: '1',
        minWidth: '140px',
      });
      const titleRow = this.el('div', {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        flexWrap: 'wrap',
      });
      if (mission.premiumOnly) {
        titleRow.appendChild(this.el('span', {
          color: COLORS.gold,
          fontSize: '11px',
          textShadow: GLOW,
          letterSpacing: '2px',
        }, '★'));
      }
      titleRow.appendChild(
        this.el('span', { fontSize: '12px', textShadow: GLOW }, mission.title),
      );
      if (mission.premiumOnly) {
        titleRow.appendChild(this.el('span', {
          color: COLORS.gold,
          fontSize: '8px',
          padding: '1px 4px',
          border: `1px solid ${COLORS.gold}`,
          letterSpacing: '1.5px',
        }, t('mission.premiumBadge')));
      }
      // Orphan rows live in the player's active log but were not part
      // of the docked station's catalogue (different station / day).
      // The badge tells the pilot "this mission isn't from here, but
      // you can still abandon it" so they aren't stuck at slot cap
      // staring at an apparently-empty list.
      if (mission.orphan) {
        titleRow.appendChild(this.el('span', {
          color: COLORS.secondary,
          fontSize: '8px',
          padding: '1px 4px',
          border: `1px solid ${COLORS.secondary}`,
          letterSpacing: '1.5px',
        }, t('mission.acceptedElsewhere')));
      }
      info.appendChild(titleRow);
      info.appendChild(
        this.el('div', {
          fontSize: '9px',
          color: COLORS.dim,
          textTransform: 'none',
          lineHeight: '1.3',
        }, mission.description),
      );

      const rewards = this.el('div', {
        display: 'flex',
        gap: '10px',
        fontSize: '9px',
      });
      rewards.appendChild(
        this.el('span', { color: COLORS.primary }, `+${mission.rewardXp} XP`),
      );
      rewards.appendChild(
        this.el('span', { color: COLORS.secondary }, `+${mission.rewardCredits}C`),
      );
      if (mission.requiredLevel > 1) {
        rewards.appendChild(
          this.el('span', { color: levelOk ? COLORS.dim : COLORS.danger },
            `LVL ${mission.requiredLevel}+`),
        );
      }
      info.appendChild(rewards);
      row.appendChild(info);

      // Right-hand action area. Four states (in priority order):
      //   1. Accepted     → ACCEPTED badge + ABANDON button (lets
      //                     the player free a slot without leaving
      //                     the station UI).
      //   2. Premium gate → ★ UNLOCK PREMIUM button (opens the
      //                     upgrade modal). Takes precedence over
      //                     level-lock so the gold CTA always wins.
      //   3. Level-locked → LVL N+ button (disabled).
      //   4. Available    → ACCEPT button. Disabled with LOG FULL
      //                     label when the pilot is already at cap.
      const actions = this.el('div', {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        flexShrink: '0',
      });

      if (isAccepted) {
        actions.appendChild(this.el('span', {
          fontSize: '10px',
          color: COLORS.primary,
          padding: '4px 8px',
          border: `1px solid ${COLORS.primary}`,
          letterSpacing: '2px',
        }, t('mission.accepted')));
        const abandonBtn = this.makeActionBtn(
          t('mission.abandon'),
          () => this.onMissionAbandon?.(mission.id),
          COLORS.danger,
        );
        actions.appendChild(abandonBtn);
      } else if (premiumGated) {
        actions.appendChild(this.makeActionBtn(
          t('mission.unlockPremium'),
          () => this.onUpgradeClick?.('shop'),
          COLORS.gold,
        ));
      } else if (!levelOk) {
        const btn = this.makeActionBtn(
          `LVL ${mission.requiredLevel}+`,
          () => { /* disabled */ },
          COLORS.danger,
        );
        btn.style.cursor = 'not-allowed';
        btn.style.opacity = '0.55';
        actions.appendChild(btn);
      } else if (atCap) {
        const btn = this.makeActionBtn(
          t('mission.logFull'),
          () => { /* disabled */ },
          COLORS.danger,
        );
        btn.style.cursor = 'not-allowed';
        btn.style.opacity = '0.65';
        actions.appendChild(btn);
      } else {
        actions.appendChild(this.makeActionBtn(
          t('mission.accept'),
          () => this.onMissionAccept?.(mission.id),
          COLORS.primary,
        ));
      }

      row.appendChild(actions);

      list.appendChild(row);
    }

    if (this.stationData!.missions.length === 0) {
      list.appendChild(
        this.el('div', { color: COLORS.dim, padding: '16px', textAlign: 'center' },
          t('mission.noneAvailable')),
      );
    }

    wrapper.appendChild(list);
    this.tabContent.appendChild(wrapper);
  }

  /* ───── Hangar ─────
   *
   * Two stacked panels:
   *   1. "Your hangar" — every hull the pilot owns. Active hull is
   *      highlighted with an ACTIVE badge; the rest get a SWITCH button.
   *   2. "Available for purchase" — every other combat-capable hull, with
   *      price, level requirement and a trait summary. BUY button is
   *      greyed out when the pilot can't afford it or is under-levelled.
   *
   * Both buttons fire server-authoritative messages; the UI refreshes
   * itself from the next `ShipHangarUpdate` / `InventoryUpdate` payload.
   */
  private renderHangar() {
    // Prefer the live accessor so buy/switch/credit changes re-render
    // correctly after a StationUI.refresh(). Falls back to the snapshot
    // passed into `show()` for callers that don't wire `getHangar`.
    const hangar = this.getHangar?.() ?? this.stationData!.hangar;
    const locale: Locale = getLocale() ?? 'en';

    const container = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '18px',
    });

    container.appendChild(
      this.renderHangarHeader(hangar),
    );
    container.appendChild(
      this.renderOwnedShips(hangar, locale),
    );
    container.appendChild(
      this.renderPurchaseList(hangar, locale),
    );

    this.tabContent.appendChild(container);
  }

  /** Compact "active ship + credits + level" summary above the lists. */
  private renderHangarHeader(hangar: HangarData): HTMLElement {
    const active = SHIP_STATS[hangar.active as ShipClass];
    const box = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,16,0,0.4)',
    });
    box.appendChild(
      this.el('div', { fontSize: '11px', color: COLORS.dim },
        t('hangar.activeLabel') + ': ' +
        (active ? t(shipNameKey(hangar.active)) : '-'),
      ),
    );
    box.appendChild(
      this.el('div', { fontSize: '11px', color: COLORS.secondary, textShadow: GLOW },
        `${hangar.credits.toLocaleString()} ${t('hud.credits')}  •  ` +
        `${t('hud.level')} ${hangar.level}`,
      ),
    );
    return box;
  }

  private renderOwnedShips(hangar: HangarData, locale: Locale): HTMLElement {
    const wrap = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });
    wrap.appendChild(
      this.el('div', {
        fontSize: '13px',
        color: COLORS.primary,
        textShadow: GLOW,
        letterSpacing: '2px',
      }, t('hangar.ownedHeader')),
    );

    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });

    const ordered = [...hangar.owned].sort((a, b) => a - b);
    for (const sc of ordered) {
      list.appendChild(this.renderOwnedRow(sc, sc === hangar.active, locale));
    }
    wrap.appendChild(list);
    return wrap;
  }

  private renderOwnedRow(
    shipClass: number,
    isActive: boolean,
    locale: Locale,
  ): HTMLElement {
    const stats = SHIP_STATS[shipClass as ShipClass];
    const row = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 12px',
      border: `1px solid ${isActive ? COLORS.primary : COLORS.dim}`,
      background: isActive ? 'rgba(0,32,0,0.5)' : 'rgba(0,8,0,0.5)',
      boxShadow: isActive ? BOX_GLOW : 'none',
      gap: '12px',
    });

    const info = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      flex: '1',
      minWidth: '0',
    });
    info.appendChild(
      this.el('div', {
        fontSize: '12px',
        textShadow: GLOW,
        letterSpacing: '1px',
      }, stats ? t(shipNameKey(shipClass)) : '—'),
    );
    if (stats) {
      info.appendChild(
        this.el('div', { fontSize: '9px', color: COLORS.dim },
          `${stats.baseHp} ${t('hangar.hull')}  •  ` +
          `${stats.baseShield} ${t('hangar.shield')}  •  ` +
          `${stats.weaponSlots}× ${t('hangar.weapon')}  •  ` +
          `${stats.cargoSlots}× ${t('hangar.cargo')}`,
        ),
      );
    }
    const summary = formatShipTraitSummary(shipClass, locale);
    if (summary.length > 0) {
      info.appendChild(
        this.el('div', { fontSize: '9px', color: COLORS.dim, fontStyle: 'italic' },
          summary.join('  •  '),
        ),
      );
    }
    row.appendChild(info);

    if (isActive) {
      const rightCol = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '6px',
        flexShrink: '0',
      });
      rightCol.appendChild(
        this.el('span', {
          fontSize: '11px',
          color: COLORS.primary,
          textShadow: GLOW,
          letterSpacing: '2px',
        }, t('hangar.active')),
      );
      // Customize button — opens the pattern + color picker modal.
      // Visible to every pilot so free pilots can browse the catalog
      // and discover the premium upgrade; the modal itself enforces
      // gating (premium patterns surface a "★ Premium required"
      // banner instead of an Apply action).
      rightCol.appendChild(
        this.makeActionBtn(t('hangar.customize'), () => {
          this.openCustomizeModal();
        }, COLORS.gold),
      );
      row.appendChild(rightCol);
    } else {
      row.appendChild(
        this.makeActionBtn(t('hangar.switch'), () => {
          this.onSwitchShip?.(shipClass);
        }),
      );
    }
    return row;
  }

  private renderPurchaseList(hangar: HangarData, locale: Locale): HTMLElement {
    const wrap = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });
    wrap.appendChild(
      this.el('div', {
        fontSize: '13px',
        color: COLORS.primary,
        textShadow: GLOW,
        letterSpacing: '2px',
      }, t('hangar.buyHeader')),
    );
    wrap.appendChild(
      this.el('div', {
        fontSize: '10px',
        color: COLORS.dim,
      }, t('hangar.buyHint')),
    );

    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });

    // Purchasable hulls: everything in SHIP_STATS except the Mothership
    // (NPC-only capital) and hulls the pilot already owns.
    const ownedSet = new Set(hangar.owned);
    const purchasable: number[] = [];
    for (const key of Object.keys(SHIP_STATS)) {
      const sc = Number(key);
      if (!Number.isFinite(sc)) continue;
      if (sc === ShipClass.Mothership) continue;
      if (ownedSet.has(sc)) continue;
      purchasable.push(sc);
    }
    purchasable.sort(
      (a, b) =>
        (SHIP_STATS[a as ShipClass]?.requiredLevel ?? 1) -
        (SHIP_STATS[b as ShipClass]?.requiredLevel ?? 1),
    );

    if (purchasable.length === 0) {
      list.appendChild(
        this.el('div', {
          fontSize: '11px',
          color: COLORS.dim,
          fontStyle: 'italic',
          padding: '8px',
        }, t('hangar.allOwned')),
      );
    } else {
      for (const sc of purchasable) {
        list.appendChild(this.renderPurchaseRow(sc, hangar, locale));
      }
    }
    wrap.appendChild(list);
    return wrap;
  }

  private renderPurchaseRow(
    shipClass: number,
    hangar: HangarData,
    locale: Locale,
  ): HTMLElement {
    const stats = SHIP_STATS[shipClass as ShipClass];
    const row = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 12px',
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,8,0,0.5)',
      gap: '12px',
    });

    const info = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      flex: '1',
      minWidth: '0',
    });
    info.appendChild(
      this.el('div', { fontSize: '12px', textShadow: GLOW, letterSpacing: '1px' },
        stats ? t(shipNameKey(shipClass)) : '—'),
    );
    if (stats) {
      info.appendChild(
        this.el('div', { fontSize: '9px', color: COLORS.dim },
          `${stats.baseHp} ${t('hangar.hull')}  •  ` +
          `${stats.baseShield} ${t('hangar.shield')}  •  ` +
          `${stats.weaponSlots}× ${t('hangar.weapon')}  •  ` +
          `${stats.cargoSlots}× ${t('hangar.cargo')}`,
        ),
      );
    }
    const summary = formatShipTraitSummary(shipClass, locale);
    if (summary.length > 0) {
      info.appendChild(
        this.el('div', { fontSize: '9px', color: COLORS.dim, fontStyle: 'italic' },
          summary.join('  •  '),
        ),
      );
    }
    row.appendChild(info);

    // Right-side stack: price + level req + BUY (disabled w/ reason if needed).
    const rightCol = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '4px',
      flexShrink: '0',
    });
    const price = stats?.creditCost ?? 0;
    const reqLevel = stats?.requiredLevel ?? 1;
    const canAfford = hangar.credits >= price;
    const hasLevel = hangar.level >= reqLevel;

    rightCol.appendChild(
      this.el('div', {
        fontSize: '11px',
        color: canAfford ? COLORS.secondary : COLORS.danger,
        textShadow: canAfford ? GLOW : `0 0 4px ${COLORS.danger}`,
      }, `${price.toLocaleString()} ${t('hud.credits')}`),
    );
    rightCol.appendChild(
      this.el('div', {
        fontSize: '9px',
        color: hasLevel ? COLORS.dim : COLORS.danger,
      }, `${t('hangar.requiredLevel')} ${reqLevel}`),
    );

    if (canAfford && hasLevel) {
      rightCol.appendChild(
        this.makeActionBtn(t('hangar.buy'), () => {
          this.onBuyShip?.(shipClass);
        }),
      );
    } else {
      const reason = !hasLevel
        ? t('hangar.levelLow', { level: reqLevel })
        : t('hangar.cantAfford');
      const btn = this.makeActionBtn(t('hangar.buy'), () => {}, COLORS.dim);
      btn.style.opacity = '0.45';
      btn.style.cursor = 'not-allowed';
      btn.title = reason;
      rightCol.appendChild(btn);
    }
    row.appendChild(rightCol);
    return row;
  }

  /* ───── Territory (sector claims) ─────
   *
   * The Territory tab is the player's main entry point into the
   * sovereignty / claim game. It needs to communicate, at a glance:
   *   1. WHO holds this sector (NPC race, another pilot, you, no-one).
   *   2. WHAT KIND of territory it is (lore-locked, frontier, hostile,
   *      yours, on cooldown).
   *   3. HOW the player can interact with it (claim, reinforce, siege,
   *      or simply read race lore).
   *   4. The player's existing holdings, with status hints.
   *
   * The renderer below builds three vertically-stacked cards:
   *   • Header — sector name, key, difficulty/zone tag, status pill.
   *   • Race card — colored block keyed off the race primary color, with
   *     race name, description, disposition, and headline trait bullets.
   *   • Action card — status-specific copy + the claim/reinforce CTA
   *     (or a "how to take this sector" primer when the action isn't
   *     available).
   * Followed by a holdings list across the player's owned sectors.
   */
  private renderTerritory() {
    const wrap = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      padding: '4px',
    });

    const tdata = this.stationData?.territory;
    if (!tdata) {
      wrap.appendChild(
        this.el('div', { color: COLORS.dim, fontSize: '11px' }, t('territory.none')),
      );
      this.tabContent.appendChild(wrap);
      return;
    }

    wrap.appendChild(this.buildTerritoryHeader(tdata));
    wrap.appendChild(this.buildTerritoryRaceCard(tdata));

    const siegeBanner = this.buildTerritorySiegeBanner(tdata);
    if (siegeBanner) wrap.appendChild(siegeBanner);

    wrap.appendChild(this.buildTerritoryStatusCard(tdata));

    if (tdata.ownedSectors.length > 0) {
      wrap.appendChild(this.buildTerritoryHoldings(tdata));
    }

    this.tabContent.appendChild(wrap);
  }

  /** Big sector name + coord + status pill. */
  private buildTerritoryHeader(tdata: TerritoryPanelData): HTMLElement {
    const head = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      paddingBottom: '8px',
      borderBottom: `1px solid ${COLORS.dim}`,
    });

    const titleRow = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '12px',
      flexWrap: 'wrap',
    });
    titleRow.appendChild(
      this.el('div', {
        fontSize: 'clamp(15px, 2vw, 22px)',
        textShadow: GLOW,
        letterSpacing: '3px',
      }, tdata.sectorName),
    );
    titleRow.appendChild(this.buildTerritoryStatusPill(tdata.status));
    head.appendChild(titleRow);

    const meta = this.el('div', {
      display: 'flex',
      gap: '14px',
      alignItems: 'center',
      fontSize: '10px',
      color: COLORS.dim,
      letterSpacing: '2px',
      flexWrap: 'wrap',
    });
    meta.appendChild(this.el('span', {}, `[${tdata.sectorKey}]`));
    // Zone tier metadata flows through the shared `ZONE_TIER_KEYS`
    // ladder so the station UI shows the same names as the universe-map
    // / warp-gate UIs (1=contested … 9=beyond). Colour is sampled from
    // the existing station palette: cool tiers stay primary-cyan, mid
    // tiers go amber, danger tiers go red. Beyond contested/embattled
    // we treat everything as "danger" because deep-tier sectors are
    // unsafe to claim or work in solo.
    const tierIdx = Math.max(0, Math.min(tdata.difficultyTier - 1, ZONE_TIER_KEYS.length - 1));
    const tierKey = ZONE_TIER_KEYS[tierIdx]!;
    const tierLabel = t(`zone.${tierKey}`);
    const tierColor = tdata.difficultyTier === 1
      ? COLORS.primary
      : tdata.difficultyTier === 2 ? COLORS.secondary : COLORS.danger;
    meta.appendChild(
      this.el('span', { color: tierColor },
        `${t('territory.tierLabel')} ${tdata.difficultyTier} · ${tierLabel}`),
    );
    head.appendChild(meta);

    return head;
  }

  private buildTerritoryStatusPill(status: TerritoryStatus): HTMLElement {
    const map: Record<TerritoryStatus, { label: string; color: string; bg: string }> = {
      frontier:    { label: t('territory.statusPill.frontier'),   color: '#000',           bg: COLORS.primary },
      yours:       { label: t('territory.statusPill.yours'),      color: '#000',           bg: COLORS.primary },
      enemy:       { label: t('territory.statusPill.enemy'),      color: '#fff',           bg: COLORS.danger },
      'lore-locked': { label: t('territory.statusPill.locked'),   color: COLORS.dim,       bg: 'transparent' },
      cooldown:    { label: t('territory.statusPill.cooldown'),   color: COLORS.secondary, bg: 'transparent' },
    };
    const cfg = map[status];
    return this.el('span', {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      fontSize: '10px',
      letterSpacing: '2px',
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.bg === 'transparent' ? cfg.color : cfg.bg}`,
      textShadow: cfg.bg === 'transparent' ? 'none' : 'none',
    }, cfg.label);
  }

  /** Race "card" — colored swatch + name + description + traits. */
  private buildTerritoryRaceCard(tdata: TerritoryPanelData): HTMLElement {
    const wrap = this.el('div', {
      display: 'flex',
      gap: '12px',
      padding: '12px',
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,8,0,0.55)',
      flexWrap: 'wrap',
    });

    if (!tdata.race) {
      // Pure frontier — no race attached. Render an "unclaimed" badge
      // to reinforce that this sector is up for grabs.
      const swatch = this.el('div', {
        width: '64px',
        height: '64px',
        flexShrink: '0',
        background: 'repeating-linear-gradient(45deg, rgba(0,255,65,0.22), rgba(0,255,65,0.22) 8px, rgba(0,0,0,0.6) 8px, rgba(0,0,0,0.6) 16px)',
        border: `1px solid ${COLORS.primary}`,
        boxShadow: BOX_GLOW,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        color: COLORS.primary,
      }, '★');
      wrap.appendChild(swatch);

      const text = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: '1',
        minWidth: '180px',
      });
      text.appendChild(this.el('div', {
        fontSize: '14px', textShadow: GLOW, letterSpacing: '2px',
      }, t('ownership.unclaimed')));
      text.appendChild(this.el('div', {
        fontSize: '11px', color: COLORS.dim, textTransform: 'none', lineHeight: '1.4',
      }, t('territory.frontierRaceBlurb')));
      wrap.appendChild(text);
      return wrap;
    }

    const race = tdata.race;
    const primaryHex = '#' + race.primaryColor.toString(16).padStart(6, '0');
    const secondaryHex = '#' + race.secondaryColor.toString(16).padStart(6, '0');

    const swatch = this.el('div', {
      width: '64px',
      height: '64px',
      flexShrink: '0',
      background: `linear-gradient(135deg, ${primaryHex} 0%, ${secondaryHex} 100%)`,
      border: `1px solid ${secondaryHex}`,
      boxShadow: `0 0 8px ${primaryHex}66, inset 0 0 6px ${secondaryHex}55`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      color: '#000',
      textShadow: `0 0 4px ${secondaryHex}`,
      letterSpacing: '1px',
      fontWeight: 'bold',
    }, race.name.slice(0, 3).toUpperCase());
    wrap.appendChild(swatch);

    const text = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      flex: '1',
      minWidth: '180px',
    });

    const titleRow = this.el('div', {
      display: 'flex',
      gap: '10px',
      alignItems: 'baseline',
      flexWrap: 'wrap',
    });
    titleRow.appendChild(this.el('div', {
      fontSize: '14px', textShadow: GLOW, letterSpacing: '2px', color: secondaryHex,
    }, race.name));
    titleRow.appendChild(this.el('span', {
      fontSize: '9px', color: COLORS.dim, letterSpacing: '2px',
    }, this.dispositionLabel(race.disposition)));
    text.appendChild(titleRow);

    text.appendChild(this.el('div', {
      fontSize: '11px', color: COLORS.dim, textTransform: 'none', lineHeight: '1.35',
    }, race.description));

    if (tdata.ownerName) {
      text.appendChild(this.el('div', {
        fontSize: '11px', color: tdata.ownerIsYou ? COLORS.primary : COLORS.danger,
        textShadow: GLOW, letterSpacing: '1px', marginTop: '2px',
      }, tdata.ownerIsYou
        ? t('territory.ownerSelf', { name: tdata.ownerName })
        : t('territory.ownerOther', { name: tdata.ownerName })));
    } else {
      text.appendChild(this.el('div', {
        fontSize: '10px', color: COLORS.dim, letterSpacing: '1px', marginTop: '2px',
      }, t('territory.ownerNpc', { race: race.name })));
    }

    if (race.traitLines.length > 0) {
      const traits = this.el('div', {
        display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px',
      });
      for (const line of race.traitLines.slice(0, 4)) {
        traits.appendChild(this.el('span', {
          fontSize: '10px',
          padding: '2px 8px',
          border: `1px solid ${primaryHex}`,
          color: secondaryHex,
          background: `${primaryHex}22`,
          textTransform: 'none',
          letterSpacing: '0.5px',
        }, line));
      }
      text.appendChild(traits);
    }

    wrap.appendChild(text);
    return wrap;
  }

  private dispositionLabel(d: 'hostile' | 'neutral' | 'friendly' | 'unknown'): string {
    switch (d) {
      case 'hostile': return t('disposition.hostile');
      case 'neutral': return t('disposition.neutral');
      case 'friendly': return t('disposition.friendly');
      default: return t('disposition.unknown');
    }
  }

  /** Active siege banner (non-null only when the sector is mid-conflict). */
  private buildTerritorySiegeBanner(tdata: TerritoryPanelData): HTMLElement | null {
    if (!tdata.siegePhase) return null;
    const remaining = tdata.siegeEndsAt
      ? Math.max(0, tdata.siegeEndsAt - Date.now())
      : 0;
    const mm = Math.floor(remaining / 60_000);
    const ss = Math.floor((remaining % 60_000) / 1000).toString().padStart(2, '0');
    const countdown = `${mm}:${ss}`;

    let title: string;
    let body: string;
    let tone: string;
    switch (tdata.siegePhase) {
      case 'contested':
        title = t('territory.siege.contestedTitle');
        body = t('territory.siege.contestedBody', {
          attacker: tdata.siegeAttackerName ?? '?',
        });
        tone = COLORS.secondary;
        break;
      case 'underSiege':
        title = t('territory.siege.underSiegeTitle');
        body = t('territory.siege.underSiegeBody', {
          attacker: tdata.siegeAttackerName ?? '?',
          countdown,
        });
        tone = COLORS.danger;
        break;
      case 'interrupted':
        title = t('territory.siege.interruptedTitle');
        body = t('territory.siege.interruptedBody');
        tone = COLORS.primary;
        break;
    }

    const box = this.el('div', {
      padding: '10px 12px',
      border: `1px solid ${tone}`,
      background: 'rgba(0,0,0,0.4)',
      boxShadow: `0 0 8px ${tone}55, inset 0 0 6px ${tone}33`,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    });
    box.appendChild(this.el('div', {
      fontSize: '12px', color: tone, textShadow: `0 0 4px ${tone}`,
      letterSpacing: '2px',
    }, title));
    box.appendChild(this.el('div', {
      fontSize: '11px', color: COLORS.dim, textTransform: 'none', lineHeight: '1.4',
    }, body));
    return box;
  }

  /** Status-driven action card (claim, reinforce, lore-locked, etc.). */
  private buildTerritoryStatusCard(tdata: TerritoryPanelData): HTMLElement {
    const card = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '12px',
      border: `1px solid ${COLORS.dim}`,
      background: 'rgba(0,8,0,0.45)',
    });

    const headline = this.el('div', {
      fontSize: '12px',
      letterSpacing: '2px',
      textShadow: GLOW,
    }, this.territoryHeadline(tdata));
    card.appendChild(headline);

    const bullets = this.territoryHowToList(tdata);
    if (bullets.length > 0) {
      const list = this.el('ul', {
        margin: '0',
        paddingLeft: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        fontSize: '11px',
        color: COLORS.dim,
        textTransform: 'none',
        lineHeight: '1.4',
      });
      for (const b of bullets) {
        const li = document.createElement('li');
        li.style.color = COLORS.dim;
        li.textContent = b;
        list.appendChild(li);
      }
      card.appendChild(list);
    }

    if (tdata.status === 'yours') {
      card.appendChild(this.buildTerritoryGarrisonRow(tdata));
      card.appendChild(this.buildOwnerPerksRow(tdata));
    }

    if (tdata.status === 'cooldown' && tdata.cooldownUntil) {
      const remaining = Math.max(0, tdata.cooldownUntil - Date.now());
      const minutes = Math.ceil(remaining / 60_000);
      card.appendChild(this.el('div', {
        fontSize: '11px', color: COLORS.secondary, letterSpacing: '1px',
      }, t('ownership.cooldownActive', { minutes })));
    }

    const action = this.buildTerritoryActionRow(tdata);
    if (action) card.appendChild(action);

    return card;
  }

  /** One-line "what's the deal" headline for the status card. */
  private territoryHeadline(tdata: TerritoryPanelData): string {
    switch (tdata.status) {
      case 'frontier':    return t('territory.headline.frontier');
      case 'yours':       return t('territory.headline.yours');
      case 'enemy':       return t('territory.headline.enemy', {
        name: tdata.ownerName ?? '?',
      });
      case 'lore-locked': return t('territory.headline.locked');
      case 'cooldown':    return t('territory.headline.cooldown');
    }
  }

  /** Bullet list rendered under the headline — explains the rules / hooks. */
  private territoryHowToList(tdata: TerritoryPanelData): string[] {
    switch (tdata.status) {
      case 'frontier':
        return [
          t('territory.howTo.frontier1', {
            cost: tdata.claimCost.toLocaleString(),
          }),
          t('territory.howTo.frontier2', {
            count: tdata.garrisonNpcsPerLevel,
          }),
          t('territory.howTo.frontier3', {
            discount: tdata.ownerShopDiscountPct,
            share: tdata.ownerRevenueSharePct,
            cap: tdata.ownerRevenueDailyCap.toLocaleString(),
          }),
        ];
      case 'yours':
        return [
          t('territory.howTo.yours1', {
            discount: tdata.ownerShopDiscountPct,
          }),
          t('territory.howTo.yours2', {
            share: tdata.ownerRevenueSharePct,
            cap: tdata.ownerRevenueDailyCap.toLocaleString(),
          }),
          t('territory.howTo.yours3', {
            window: tdata.siegeWindowMinutes,
          }),
        ];
      case 'enemy':
        return [
          t('territory.howTo.enemy1'),
          t('territory.howTo.enemy2', { window: tdata.siegeWindowMinutes }),
          t('territory.howTo.enemy3'),
        ];
      case 'lore-locked':
        return [
          t('territory.howTo.locked1'),
          t('territory.howTo.locked2'),
        ];
      case 'cooldown':
        return [
          t('territory.howTo.cooldown1'),
        ];
    }
  }

  private buildTerritoryGarrisonRow(tdata: TerritoryPanelData): HTMLElement {
    const wrap = this.el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap',
      marginTop: '4px',
    });
    wrap.appendChild(this.el('span', {
      fontSize: '11px', color: COLORS.dim, letterSpacing: '2px',
    }, t('garrison.levelLabel')));

    const bar = this.el('div', { display: 'flex', gap: '3px' });
    for (let i = 1; i <= tdata.garrisonLevelMax; i++) {
      const seg = this.el('div', {
        width: '22px',
        height: '10px',
        background: i <= tdata.garrisonLevel ? COLORS.primary : 'transparent',
        border: `1px solid ${COLORS.dim}`,
        boxShadow: i <= tdata.garrisonLevel ? `0 0 4px ${COLORS.primary}` : 'none',
      });
      bar.appendChild(seg);
    }
    wrap.appendChild(bar);

    wrap.appendChild(this.el('span', {
      fontSize: '11px', color: COLORS.primary, textShadow: GLOW,
    }, `${tdata.garrisonLevel} / ${tdata.garrisonLevelMax}`));

    const npcCount = tdata.garrisonLevel * tdata.garrisonNpcsPerLevel;
    wrap.appendChild(this.el('span', {
      fontSize: '10px', color: COLORS.dim, letterSpacing: '1px',
    }, t('territory.garrisonNpcs', { count: npcCount })));

    return wrap;
  }

  private buildOwnerPerksRow(tdata: TerritoryPanelData): HTMLElement {
    const row = this.el('div', {
      display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px',
    });
    const perks = [
      `${tdata.ownerShopDiscountPct}% ${t('territory.perks.shop')}`,
      `${tdata.ownerRevenueSharePct}% ${t('territory.perks.share', { cap: tdata.ownerRevenueDailyCap.toLocaleString() })}`,
    ];
    for (const p of perks) {
      row.appendChild(this.el('span', {
        fontSize: '10px',
        padding: '2px 8px',
        border: `1px solid ${COLORS.secondary}`,
        color: COLORS.secondary,
        background: 'rgba(255,176,0,0.08)',
        textTransform: 'none',
        letterSpacing: '0.5px',
      }, p));
    }
    return row;
  }

  private buildTerritoryActionRow(tdata: TerritoryPanelData): HTMLElement | null {
    if (tdata.canClaim) {
      const row = this.el('div', {
        display: 'flex', flexWrap: 'wrap', gap: '10px',
        alignItems: 'center', marginTop: '4px',
      });
      row.appendChild(this.el('span', {
        fontSize: '11px', color: COLORS.secondary, textShadow: `0 0 4px ${COLORS.secondary}`,
      }, `${t('claim.costLabel')}: ${tdata.claimCost.toLocaleString()}C`));
      row.appendChild(
        this.makeActionBtn(t('claim.claimSector'), () => {
          this.onClaimSector?.(tdata.sectorKey);
        }, COLORS.secondary),
      );
      return row;
    }

    if (tdata.canReinforce && tdata.targetGarrisonLevel != null) {
      const row = this.el('div', {
        display: 'flex', flexWrap: 'wrap', gap: '10px',
        alignItems: 'center', marginTop: '4px',
      });
      row.appendChild(this.el('span', {
        fontSize: '11px', color: COLORS.secondary, textShadow: `0 0 4px ${COLORS.secondary}`,
      }, `${t('claim.reinforceCost')}: ${(tdata.reinforceCost ?? 0).toLocaleString()}C → ${t('garrison.levelLabel')} ${tdata.targetGarrisonLevel}`));
      row.appendChild(
        this.makeActionBtn(t('claim.reinforce'), () => {
          this.onReinforceGarrison?.(tdata.sectorKey, tdata.targetGarrisonLevel!);
        }, COLORS.secondary),
      );
      return row;
    }

    if (tdata.hint) {
      return this.el('div', {
        fontSize: '10px', color: COLORS.dim, textTransform: 'none', marginTop: '4px',
      }, tdata.hint);
    }

    return null;
  }

  /** Player's "your holdings" list, rendered with race tints. */
  private buildTerritoryHoldings(tdata: TerritoryPanelData): HTMLElement {
    const wrap = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      marginTop: '6px',
    });
    wrap.appendChild(this.el('div', {
      fontSize: '12px', textShadow: GLOW, letterSpacing: '2px',
    }, t('territory.ownedBy')));

    for (const row of tdata.ownedSectors) {
      const tint = row.raceColor !== undefined
        ? '#' + row.raceColor.toString(16).padStart(6, '0')
        : COLORS.dim;
      const line = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 8px',
        border: `1px solid ${row.isCurrent ? COLORS.primary : COLORS.dim}`,
        background: row.isCurrent ? 'rgba(0,32,0,0.5)' : 'rgba(0,8,0,0.35)',
        boxShadow: row.isCurrent ? BOX_GLOW : 'none',
        fontSize: '10px',
        gap: '8px',
      });
      const left = this.el('div', {
        display: 'flex', alignItems: 'center', gap: '8px',
        flex: '1', minWidth: '0',
      });
      left.appendChild(this.el('span', {
        display: 'inline-block',
        width: '8px', height: '8px', borderRadius: '50%',
        background: tint, boxShadow: `0 0 4px ${tint}`, flexShrink: '0',
      }));
      left.appendChild(this.el('span', {}, row.label));
      if (row.isCurrent) {
        left.appendChild(this.el('span', {
          color: COLORS.primary, letterSpacing: '2px', fontSize: '9px',
        }, t('territory.hereLabel')));
      }
      line.appendChild(left);
      const right = this.el('div', {
        display: 'flex', gap: '8px', alignItems: 'center', flexShrink: '0',
      });
      if (row.garrisonLevel !== undefined) {
        right.appendChild(this.el('span', {
          color: COLORS.dim, letterSpacing: '1px', fontSize: '9px',
        }, `G${row.garrisonLevel}`));
      }
      right.appendChild(this.el('span', {
        color: COLORS.secondary,
      }, row.statusLabel));
      line.appendChild(right);
      wrap.appendChild(line);
    }

    return wrap;
  }

  /* ───── Marketplace ─────
   *
   * The actual marketplace UI lives in `MarketplacePanel` and is owned
   * by `main.ts` (so it can survive tab switches and keep callbacks
   * wired to the network client). The station tab just mounts the
   * panel's root inside `tabContent` while the MARKETPLACE tab is
   * active and unmounts on tab change in `renderTab()`.
   */
  /**
   * Codex tab (Phase 2.3). Renders the pilot's unlocked entries with
   * a "discovered N / M" header. Locked entries are intentionally NOT
   * listed — the codex is a reward for exploration; revealing the
   * full list up front would gut the discovery loop.
   */
  private renderCodex() {
    const unlocked = this.getUnlockedCodex?.() ?? new Set<string>();
    const all = listCodexEntries();
    // Resolve unlocked ids to entries via the registry (handles
    // procedural per-ruin `lore_<kind>_<idx>` fallbacks).
    const shown: { id: string; title: string; body: string; category: string }[] = [];
    const seenIds = new Set<string>();
    for (const id of unlocked) {
      const e = getCodexEntry(id);
      if (!e) continue;
      if (seenIds.has(e.id)) continue;
      seenIds.add(e.id);
      shown.push(e);
    }

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: `1px solid ${COLORS.dim}`,
      marginBottom: '12px',
    });
    header.appendChild(
      this.el('div', {
        color: COLORS.primary,
        fontSize: '14px',
        textTransform: 'uppercase',
        letterSpacing: '2px',
      }, t('station.codex')),
    );
    header.appendChild(
      this.el('div', {
        color: COLORS.dim,
        fontSize: '11px',
      }, `${shown.length} / ${all.length}`),
    );
    this.tabContent.appendChild(header);

    if (shown.length === 0) {
      this.tabContent.appendChild(
        this.el('div', {
          color: COLORS.dim,
          fontSize: '11px',
          padding: '40px 8px',
          textAlign: 'center',
          textTransform: 'none',
        }, 'No codex fragments recovered yet. Investigate ruins on planetary surfaces (hold E within range) to unlock entries.'),
      );
      return;
    }

    for (const entry of shown) {
      const card = this.el('div', {
        padding: '12px',
        border: `1px solid ${COLORS.dim}`,
        marginBottom: '8px',
        background: 'rgba(20,20,30,0.4)',
      });
      card.appendChild(this.el('div', {
        color: COLORS.primary,
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        marginBottom: '4px',
      }, entry.title));
      card.appendChild(this.el('div', {
        color: COLORS.dim,
        fontSize: '10px',
        textTransform: 'uppercase',
        marginBottom: '8px',
      }, entry.category));
      const body = this.el('div', {
        color: '#cfd8e3',
        fontSize: '12px',
        lineHeight: '1.5',
        textTransform: 'none',
        whiteSpace: 'pre-wrap',
      });
      body.textContent = entry.body;
      card.appendChild(body);
      this.tabContent.appendChild(card);
    }
  }

  private renderMarketplace() {
    if (!this.marketplaceView) {
      this.tabContent.appendChild(
        this.el('div', {
          color: COLORS.dim,
          fontSize: '11px',
          textAlign: 'center',
          padding: '40px 8px',
          textTransform: 'none',
        }, t('marketplace.empty')),
      );
      return;
    }
    this.marketplaceView.mount(this.tabContent);
  }

  /* ───── Bounty board ─────
   *
   * Shared inter-station NPC kill contract list. Server is the sole
   * authority on what's posted; this tab is read-only — the player
   * "claims" a contract by killing the matching NPC anywhere in the
   * galaxy (the credit + bonus XP land via the regular kill flow plus
   * a system chat broadcast). Refreshes are pushed by the server on
   * every dock + every scheduled `bounty_board_refresh` fire, so we
   * just re-render whatever the cached snapshot currently holds.
   */
  private renderBountyBoard(): void {
    const wrap = this.el('div', {
      display: 'flex', flexDirection: 'column', gap: '12px',
      padding: '4px 0',
    });
    this.tabContent.appendChild(wrap);

    const heading = this.el('div', {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '12px', flexWrap: 'wrap',
    });
    heading.appendChild(this.el('div', {
      fontSize: 'clamp(14px, 1.6vw, 18px)',
      letterSpacing: '3px',
      textShadow: GLOW,
    }, t('station.bounty')));
    heading.appendChild(this.el('div', {
      color: COLORS.dim,
      fontSize: '10px',
      letterSpacing: '1px',
      textTransform: 'none',
      maxWidth: '60%',
      textAlign: 'right',
    }, t('bounty.tabHint')));
    wrap.appendChild(heading);

    const snap = this.getBountyContracts?.() ?? { contracts: [], serverTime: Date.now() };
    const now = Date.now();
    // Drift between server snapshot time and local clock — used to
    // render expiry countdowns correctly on machines whose wall clock
    // is off by minutes. Computed once per render.
    const clientToServerOffset = (snap.serverTime || now) - now;

    const active = snap.contracts.filter((c) => c.expiresAt > snap.serverTime);
    if (active.length === 0) {
      wrap.appendChild(this.el('div', {
        color: COLORS.dim,
        fontSize: '12px',
        textAlign: 'center',
        padding: '40px 8px',
        textTransform: 'none',
      }, t('bounty.empty')));
      return;
    }

    const grid = this.el('div', {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}, 1fr))`,
      gap: '12px',
    });
    wrap.appendChild(grid);

    for (const contract of active) {
      grid.appendChild(this.buildBountyCard(contract, now + clientToServerOffset));
    }
  }

  private buildBountyCard(contract: SystemBountyContract, serverNow: number): HTMLElement {
    const card = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '12px',
      background: 'rgba(0,16,0,0.45)',
      border: `1px solid ${COLORS.dim}`,
      boxShadow: BOX_GLOW,
      minWidth: '0',
    });

    const titleRow = this.el('div', {
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      gap: '8px',
    });
    titleRow.appendChild(this.el('div', {
      color: COLORS.danger,
      fontSize: '10px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
    }, t('bounty.target')));
    titleRow.appendChild(this.el('div', {
      color: COLORS.secondary,
      fontSize: '10px',
      letterSpacing: '1px',
    }, t('bounty.level', { level: contract.npcLevel })));
    card.appendChild(titleRow);

    card.appendChild(this.el('div', {
      color: COLORS.primary,
      fontSize: 'clamp(13px, 1.5vw, 16px)',
      textShadow: GLOW,
      letterSpacing: '1px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }, contract.npcName));

    if (contract.npcShipClass) {
      card.appendChild(this.el('div', {
        color: COLORS.dim,
        fontSize: '10px',
        letterSpacing: '1px',
        textTransform: 'uppercase',
      }, t('bounty.shipClass', { shipClass: contract.npcShipClass })));
    }

    const rewardWrap = this.el('div', {
      display: 'flex', flexDirection: 'column', gap: '2px',
      paddingTop: '4px',
      borderTop: `1px dashed ${COLORS.dim}`,
    });
    rewardWrap.appendChild(this.el('div', {
      color: COLORS.secondary,
      fontSize: '11px',
      letterSpacing: '1px',
    }, `${t('bounty.reward')}: ${contract.rewardCredits.toLocaleString()}C`));
    rewardWrap.appendChild(this.el('div', {
      color: COLORS.primary,
      fontSize: '10px',
      letterSpacing: '1px',
    }, t('bounty.bonusXp', { xp: contract.rewardXp })));
    card.appendChild(rewardWrap);

    const remainingMs = Math.max(0, contract.expiresAt - serverNow);
    card.appendChild(this.el('div', {
      color: remainingMs <= 60_000 ? COLORS.danger : COLORS.dim,
      fontSize: '10px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    }, remainingMs > 0
      ? t('bounty.expiresIn', { time: formatBountyTimeRemaining(remainingMs) })
      : t('bounty.expired')));

    card.appendChild(this.el('div', {
      color: COLORS.dim,
      fontSize: '9px',
      letterSpacing: '1px',
      textTransform: 'none',
      paddingTop: '4px',
    }, t('bounty.howToClaim')));

    return card;
  }

  /* ═══════════════ PUBLIC API ═══════════════ */
  show(data: StationData) {
    this.stationData = data;
    // Append reputation + liberator suffix in the header when this is
    // a surface base (Phase 3.2 / 3.3).
    let suffix = '';
    if (data.surfaceId) {
      const rep = this.getPlanetReputation?.(data.surfaceId) ?? 0;
      const liberated = this.getLiberatedSurfaces?.().has(data.surfaceId) ?? false;
      suffix = `  •  REP ${rep >= 0 ? '+' : ''}${rep}`;
      if (liberated) suffix += '  •  LIBERATED';
    }
    this.stationNameEl.textContent = data.name + suffix;
    this.shopFilter = 'all';
    this.shopPage = 0;
    this.craftFilter = 'all';
    this.craftPage = 0;
    this.updateHeaderCredits();
    this.switchTab('shop');
    this.root.style.display = 'flex';
  }

  /**
   * Replaces the shop grid with the server-authoritative catalogue (race
   * rares, owner discount already in `price`).
   */
  applyShopSnapshot(msg: StationShopSnapshotMsg): void {
    if (!this.stationData) return;
    const weaponLabel = t('station.catWeapon');
    const shieldLabel = t('station.catShield');
    const engineLabel = t('station.catEngine');
    const consumableLabel = t('station.catConsumable');
    const unlimitedLabel = t('shop.unlimited');

    const shopItems: ShopItem[] = [];
    // Premium rows replace the per-category badge ("12 SHOTS", "PERMANENT",
    // …) with the gold "★ PREMIUM ONLY" label so the gold border + Unlock
    // CTA on the card have a matching headline. Without this swap, free
    // pilots saw the regular badge and had no on-card cue that the row
    // was a subscription perk (only the description hinted at it).
    const premiumBadge = t('shop.premiumOnly');
    for (const row of msg.items) {
      const w = lookupWeapon(row.itemId);
      if (w) {
        shopItems.push({
          itemId: row.itemId,
          name: itemLabel('weapon', row.itemId, w.name),
          price: row.price,
          requiredLevel: w.requiredLevel,
          category: weaponLabel,
          categoryKey: 'weapon',
          description: itemDescription('weapon', row.itemId, w.description ?? ''),
          extraBadge: row.premiumOnly
            ? premiumBadge
            : w.maxAmmo > 0
              ? t('shop.shots', { n: w.maxAmmo })
              : unlimitedLabel,
          premiumOnly: row.premiumOnly,
        });
        continue;
      }
      const s = lookupShield(row.itemId);
      if (s) {
        shopItems.push({
          itemId: row.itemId,
          name: itemLabel('shield', row.itemId, s.name),
          price: row.price,
          requiredLevel: s.requiredLevel,
          category: shieldLabel,
          categoryKey: 'shield',
          description: itemDescription('shield', row.itemId, s.description ?? ''),
          extraBadge: row.premiumOnly
            ? premiumBadge
            : s.maxCharges > 0
              ? t('shop.charges', { n: s.maxCharges })
              : unlimitedLabel,
          premiumOnly: row.premiumOnly,
        });
        continue;
      }
      const e = lookupEngine(row.itemId);
      if (e) {
        shopItems.push({
          itemId: row.itemId,
          name: itemLabel('engine', row.itemId, e.name),
          price: row.price,
          requiredLevel: e.requiredLevel,
          category: engineLabel,
          categoryKey: 'engine',
          description: itemDescription('engine', row.itemId, e.description ?? ''),
          extraBadge: row.premiumOnly ? premiumBadge : t('shop.permanent'),
          premiumOnly: row.premiumOnly,
        });
        continue;
      }
      const c = CONSUMABLES[row.itemId];
      if (c) {
        shopItems.push({
          itemId: row.itemId,
          name: itemLabel('consumable', row.itemId, c.name),
          price: row.price,
          requiredLevel: c.requiredLevel,
          category: consumableLabel,
          categoryKey: 'consumable',
          description: itemDescription('consumable', row.itemId, c.description),
          extraBadge: row.premiumOnly ? premiumBadge : t('shop.singleUse'),
          premiumOnly: row.premiumOnly,
        });
      }
    }
    this.stationData.shopItems = shopItems;
    if (msg.discountPct > 0) {
      this.stationNameEl.textContent =
        `${this.stationData.name}${msg.isOwner ? ` — ${t('ownership.ownedBy', { owner: msg.ownerName ?? 'YOU' })}` : ''}`;
    }
    if (this.activeTab === 'shop') this.renderTab();
  }

  applyMissionsFromServer(
    rows: Array<{
      id: string;
      title: string;
      description: string;
      requiredLevel: number;
      rewards: { xp: number; credits: number };
      premiumOnly?: boolean;
    }>,
  ): void {
    if (!this.stationData) return;
    const activeIds = this.getActiveMissionIds?.() ?? new Set<string>();
    const catalogueRows: MissionData[] = rows.map((m) => ({
      id: m.id,
      title: m.title,
      description: m.description,
      rewardXp: m.rewards.xp,
      rewardCredits: m.rewards.credits,
      requiredLevel: m.requiredLevel,
      isActive: activeIds.has(m.id),
      premiumOnly: m.premiumOnly === true,
    }));

    // Build "orphan" rows for active missions the docked station no
    // longer offers in its catalogue. Without this, the slot counter
    // can read 5/5 while the tab only shows 4 ACCEPTED rows — the
    // missing accepted mission was either accepted at a different
    // station or its daily/weekly id has since rolled over. Surfacing
    // these here lets the pilot abandon them without leaving the
    // station UI.
    const cataloguedIds = new Set(catalogueRows.map((r) => r.id));
    const allActive = this.getActiveMissions?.() ?? [];
    const orphanRows: MissionData[] = allActive
      .filter((m) => !cataloguedIds.has(m.id))
      .map((m) => ({
        id: m.id,
        title: m.title,
        description: m.description,
        rewardXp: m.rewards.xp,
        rewardCredits: m.rewards.credits,
        requiredLevel: m.requiredLevel,
        isActive: true,
        orphan: true,
        premiumOnly: m.premiumOnly === true,
      }));

    this.stationData.missions = [...orphanRows, ...catalogueRows];
    if (this.activeTab === 'missions') this.renderTab();
  }

  /**
   * Re-evaluate `isActive` on the cached station mission list and re-render
   * if currently visible. Called from main.ts when the player's active
   * mission log changes (Accept / Complete / Abandon / Expire) so the
   * "ACCEPT" buttons grey out without waiting for a fresh station catalogue.
   *
   * Also rebuilds the "orphan" prefix rows from the live active log so
   * a mission accepted at this station that's NOT in the catalogue (or
   * one abandoned via the journal HUD) appears / disappears here too.
   */
  refreshMissionStatus(): void {
    if (!this.stationData) return;
    const missions = this.stationData.missions;
    if (!missions) return;
    const activeIds = this.getActiveMissionIds?.() ?? new Set<string>();

    // Drop any previously-injected orphan rows; we'll rebuild them
    // from scratch below so abandoned missions stop appearing here
    // and newly-accepted-elsewhere missions show up.
    const catalogueRows = missions.filter((m) => !m.orphan);
    let changed = missions.length !== catalogueRows.length;
    for (const m of catalogueRows) {
      const next = activeIds.has(m.id);
      if (m.isActive !== next) {
        m.isActive = next;
        changed = true;
      }
    }

    const cataloguedIds = new Set(catalogueRows.map((r) => r.id));
    const allActive = this.getActiveMissions?.() ?? [];
    const orphanRows: MissionData[] = allActive
      .filter((m) => !cataloguedIds.has(m.id))
      .map((m) => ({
        id: m.id,
        title: m.title,
        description: m.description,
        rewardXp: m.rewards.xp,
        rewardCredits: m.rewards.credits,
        requiredLevel: m.requiredLevel,
        isActive: true,
        orphan: true,
        premiumOnly: m.premiumOnly === true,
      }));
    if (orphanRows.length > 0) changed = true;

    this.stationData.missions = [...orphanRows, ...catalogueRows];
    if (changed && this.activeTab === 'missions') this.renderTab();
  }

  updateTerritory(data: TerritoryPanelData | undefined): void {
    if (!this.stationData) return;
    this.stationData.territory = data;
    if (this.activeTab === 'territory') this.renderTab();
  }

  hide() {
    // Detach the marketplace view before hiding the station UI so the
    // panel can drop its container reference. Otherwise its `refresh()`
    // would keep mutating an off-screen, undocked-state DOM node until
    // the next dock event re-mounts it.
    this.marketplaceView?.unmount();
    this.closeCustomizeModal();
    this.root.style.display = 'none';
    this.stationData = null;
  }

  /* ───── Hangar — Customize modal ─────
   *
   * PREMIUM-only ship pattern + color picker. Mounted as an overlay
   * on top of the station UI (not a separate route) so the modal
   * can read the live hangar / cosmetics state without any
   * cross-component plumbing. Free pilots can browse the catalog
   * (see what subscribing unlocks) but the only Apply path open to
   * them is "default solid" — premium patterns surface a banner
   * that hands off to the global upgrade modal.
   */
  private customizeModalEl: HTMLElement | null = null;
  private customizePreview: ShipCardPreviewHandle | null = null;
  private customizeSelection: PlayerShipCosmetics = { ...DEFAULT_SHIP_COSMETICS };

  private openCustomizeModal(): void {
    const hangar = this.getHangar?.();
    if (!hangar) return;
    this.closeCustomizeModal();
    const isPremium = this.isPremium?.() ?? false;
    const activeSlug = (SHIP_CLASS_KEY as Record<number, string>)[hangar.active] ?? 'fighter';
    // Hull color is locked to the pilot's race (see PlayerShipCosmetics
    // docstring for the PvP rationale). Resolve it once here so the
    // preview thumbnail and the pattern swatches all draw against the
    // pilot's actual race plate color, not a generic placeholder.
    const racePrimary = hangar.raceId !== null
      ? (RACE_INFO as Record<number, { primaryColor?: number }>)[hangar.raceId]?.primaryColor
        ?? DEFAULT_SHIP_PRIMARY_COLOR
      : DEFAULT_SHIP_PRIMARY_COLOR;
    this.customizeSelection = {
      pattern: hangar.cosmetics.pattern,
      secondaryColor: hangar.cosmetics.secondaryColor,
    };

    const overlay = this.el('div', {
      position: 'fixed',
      inset: '0',
      background: 'rgba(0,0,0,0.78)',
      zIndex: '200',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      backdropFilter: 'blur(2px)',
    });
    overlay.addEventListener('click', (ev) => {
      if (ev.target === overlay) this.closeCustomizeModal();
    });

    const panel = this.el('div', {
      width: 'min(960px, 96vw)',
      maxHeight: '92vh',
      background: COLORS.bgSolid,
      border: `1px solid ${COLORS.gold}`,
      boxShadow: `0 0 32px rgba(255,215,0,0.35)`,
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      padding: '18px',
      overflow: 'hidden',
      fontFamily: FONT,
      color: COLORS.primary,
      letterSpacing: '1px',
    });

    // Header
    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
    });
    header.appendChild(
      this.el('div', {
        fontSize: '15px',
        color: COLORS.gold,
        textShadow: GLOW,
      }, t('hangar.customizeTitle')),
    );
    const cancelBtn = this.makeActionBtn(t('hangar.customizeCancel'), () => {
      this.closeCustomizeModal();
    }, COLORS.dim);
    header.appendChild(cancelBtn);
    panel.appendChild(header);

    panel.appendChild(
      this.el('div', { fontSize: '11px', color: COLORS.dim }, t('hangar.customizeIntro')),
    );

    // Body: 2-column layout (preview | controls)
    const body = this.el('div', {
      display: 'grid',
      gridTemplateColumns: 'minmax(220px, 320px) 1fr',
      gap: '18px',
      flex: '1',
      minHeight: '0',
    });

    // ── Preview column ──
    const previewCol = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    });
    const previewWrap = this.el('div', {
      width: '100%',
      aspectRatio: '1',
      border: `1px solid ${COLORS.dim}`,
      background: 'radial-gradient(circle at 50% 40%, #001020 0%, #000308 70%, #000 100%)',
      position: 'relative',
    });
    const previewCanvas = document.createElement('canvas');
    previewCanvas.style.width = '100%';
    previewCanvas.style.height = '100%';
    previewCanvas.style.display = 'block';
    previewWrap.appendChild(previewCanvas);
    previewCol.appendChild(previewWrap);
    previewCol.appendChild(
      this.el('div', { fontSize: '10px', color: COLORS.dim, textAlign: 'center' },
        SHIP_STATS[hangar.active as ShipClass]?.name ?? '—'),
    );

    body.appendChild(previewCol);

    // ── Controls column ──
    const controls = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      overflow: 'auto',
      paddingRight: '4px',
    });

    // Pattern picker
    const patternHeader = this.el('div', {
      fontSize: '12px',
      color: COLORS.primary,
      textShadow: GLOW,
    }, t('hangar.customizePattern'));
    controls.appendChild(patternHeader);

    const patternGrid = this.el('div', {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
      gap: '8px',
    });
    const patternCardEls = new Map<ShipPatternId, HTMLElement>();
    for (const preset of SHIP_PATTERN_PRESETS) {
      const locked = preset.premiumOnly && !isPremium;
      const card = this.el('div', {
        border: `1px solid ${locked ? COLORS.gold : COLORS.dim}`,
        background: 'rgba(0,8,0,0.55)',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        cursor: 'pointer',
        position: 'relative',
        opacity: locked ? '0.85' : '1',
      });
      // Pattern swatch — same procedural drawer the in-game hull
      // uses, scaled down. Pre-rendered at modal-open time so the
      // grid doesn't flicker while the user picks colors.
      const swatch = document.createElement('canvas');
      swatch.width = 96;
      swatch.height = 64;
      swatch.style.width = '100%';
      swatch.style.height = '64px';
      swatch.style.display = 'block';
      drawPatternSwatch(swatch, preset.id, racePrimary, this.customizeSelection.secondaryColor);
      card.appendChild(swatch);
      card.appendChild(
        this.el('div', { fontSize: '10px', color: COLORS.primary, textAlign: 'center' },
          t(`cosmetics.pattern.${preset.id}.name`)),
      );
      if (preset.premiumOnly) {
        card.appendChild(
          this.el('div', {
            position: 'absolute',
            top: '4px',
            right: '4px',
            fontSize: '8px',
            color: COLORS.gold,
            textShadow: '0 0 4px rgba(255,215,0,0.8)',
            letterSpacing: '1px',
          }, '★ ' + t('hangar.customizePremiumOnly')),
        );
      }
      card.addEventListener('click', () => {
        if (locked) {
          // Hand off to the global upgrade modal — same flow as the
          // shop's premium-only items.
          this.onUpgradeClick?.('shop');
          return;
        }
        this.customizeSelection.pattern = preset.id;
        for (const [pid, el] of patternCardEls) {
          el.style.borderColor = pid === preset.id ? COLORS.primary : (locked ? COLORS.gold : COLORS.dim);
          el.style.boxShadow = pid === preset.id ? BOX_GLOW : 'none';
        }
        this.refreshCustomizePreview(activeSlug, racePrimary);
      });
      // Mark currently selected
      if (preset.id === this.customizeSelection.pattern) {
        card.style.borderColor = COLORS.primary;
        card.style.boxShadow = BOX_GLOW;
      }
      patternCardEls.set(preset.id, card);
      patternGrid.appendChild(card);
    }
    controls.appendChild(patternGrid);

    // Accent color picker. Hull color is intentionally NOT
    // customizable — see PlayerShipCosmetics docstring; it stays
    // pinned to the race so PvP silhouettes always read as the
    // right civilization.
    const colorRow = this.el('div', {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '12px',
    });
    const refreshSwatches = () => {
      for (const [pid, el] of patternCardEls) {
        const sw = el.querySelector('canvas') as HTMLCanvasElement | null;
        if (sw) drawPatternSwatch(
          sw, pid,
          racePrimary,
          this.customizeSelection.secondaryColor,
        );
      }
    };
    const makeColorPicker = (
      labelKey: string,
      initial: number,
      onChange: (n: number) => void,
    ): HTMLElement => {
      const wrap = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      });
      wrap.appendChild(
        this.el('div', { fontSize: '10px', color: COLORS.dim }, t(labelKey)),
      );
      const input = document.createElement('input');
      input.type = 'color';
      input.value = '#' + initial.toString(16).padStart(6, '0');
      input.style.width = '100%';
      input.style.height = '36px';
      input.style.border = `1px solid ${COLORS.dim}`;
      input.style.background = 'transparent';
      input.style.cursor = isPremium ? 'pointer' : 'not-allowed';
      input.disabled = !isPremium;
      input.addEventListener('input', () => {
        const n = parseInt(input.value.slice(1), 16);
        if (Number.isFinite(n)) {
          onChange(n);
          refreshSwatches();
          this.refreshCustomizePreview(activeSlug, racePrimary);
        }
      });
      wrap.appendChild(input);
      return wrap;
    };
    colorRow.appendChild(
      makeColorPicker(
        'hangar.customizeSecondary',
        this.customizeSelection.secondaryColor,
        (n) => { this.customizeSelection.secondaryColor = n; },
      ),
    );
    controls.appendChild(colorRow);

    if (!isPremium) {
      // Premium-required banner — shown for free pilots so they
      // understand why colors / non-default patterns are inert.
      const banner = this.el('div', {
        marginTop: '6px',
        padding: '10px 12px',
        border: `1px solid ${COLORS.gold}`,
        background: 'rgba(255,215,0,0.06)',
        color: COLORS.gold,
        fontSize: '11px',
        cursor: 'pointer',
        textShadow: '0 0 4px rgba(255,215,0,0.4)',
      }, '★ ' + t('hangar.customizePremiumNeeded'));
      banner.addEventListener('click', () => this.onUpgradeClick?.('shop'));
      controls.appendChild(banner);
    }

    body.appendChild(controls);
    panel.appendChild(body);

    // Footer with Reset / Apply
    const footer = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      paddingTop: '8px',
      borderTop: `1px solid ${COLORS.dim}`,
    });
    footer.appendChild(
      this.makeActionBtn(t('hangar.customizeReset'), () => {
        this.customizeSelection = {
          pattern: DEFAULT_SHIP_PATTERN,
          secondaryColor: DEFAULT_SHIP_SECONDARY_COLOR,
        };
        this.onSetShipCosmetics?.({ ...this.customizeSelection });
        this.closeCustomizeModal();
      }, COLORS.dim),
    );
    const applyBtn = this.makeActionBtn(t('hangar.customizeApply'), () => {
      this.onSetShipCosmetics?.({ ...this.customizeSelection });
      this.closeCustomizeModal();
    }, isPremium ? COLORS.primary : COLORS.dim);
    if (!isPremium) {
      // Free pilots can still hit Apply, but only the default
      // bundle has any effect server-side; styling the button as
      // disabled communicates that without a second control.
      applyBtn.style.opacity = '0.6';
      applyBtn.style.cursor = 'not-allowed';
    }
    footer.appendChild(applyBtn);
    panel.appendChild(footer);

    overlay.appendChild(panel);
    this.container.appendChild(overlay);
    this.customizeModalEl = overlay;

    // Mount the live preview AFTER the overlay is in the DOM so the
    // canvas has actual dimensions for the WebGL renderer to size
    // against. Otherwise the preview comes up as a 0×0 viewport on
    // first frame and stays blank until the user resizes the window.
    this.customizePreview = mountShipCardPreview(previewCanvas, {
      shipClass: hangar.active,
      rotating: true,
      primaryColor: racePrimary,
      secondaryColor: this.customizeSelection.secondaryColor,
      thrusterColor: this.customizeSelection.secondaryColor,
      cockpitColor: this.customizeSelection.secondaryColor,
      pattern: this.customizeSelection.pattern,
      emissiveBoost: 0.3,
    });
  }

  private refreshCustomizePreview(shipSlug: string, racePrimary: number): void {
    if (!this.customizePreview) return;
    this.customizePreview.setShip(shipSlug, {
      primaryColor: racePrimary,
      secondaryColor: this.customizeSelection.secondaryColor,
      thrusterColor: this.customizeSelection.secondaryColor,
      cockpitColor: this.customizeSelection.secondaryColor,
      pattern: this.customizeSelection.pattern,
      emissiveBoost: 0.3,
    });
  }

  private closeCustomizeModal(): void {
    if (this.customizePreview) {
      this.customizePreview.dispose();
      this.customizePreview = null;
    }
    if (this.customizeModalEl && this.customizeModalEl.parentElement) {
      this.customizeModalEl.parentElement.removeChild(this.customizeModalEl);
    }
    this.customizeModalEl = null;
  }
}

/**
 * Render a small flat thumbnail of the requested pattern into a
 * canvas. Mirrors `drawShipPatternTo` in `ship-builder.ts` (using the
 * tiny inline 2D drawing routines below) — kept as a local copy so
 * the modal doesn't take a hard dependency on the ship-builder
 * module just to draw a 96×64 swatch.
 */
function drawPatternSwatch(
  canvas: HTMLCanvasElement,
  pattern: ShipPatternId,
  primary: number,
  secondary: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const primaryHex = '#' + primary.toString(16).padStart(6, '0');
  const secondaryHex = '#' + secondary.toString(16).padStart(6, '0');
  ctx.fillStyle = primaryHex;
  ctx.fillRect(0, 0, w, h);
  if (pattern === 'stripes') {
    ctx.fillStyle = secondaryHex;
    ctx.fillRect(0, h * 0.2, w, h * 0.12);
    ctx.fillRect(0, h * 0.68, w, h * 0.12);
  } else if (pattern === 'hex') {
    ctx.strokeStyle = secondaryHex;
    ctx.lineWidth = 1;
    const r = h / 6;
    const cw = Math.sqrt(3) * r;
    const ch = 1.5 * r;
    for (let row = 0, y = 0; y < h + ch; row++, y += ch) {
      const xOff = (row & 1) ? cw * 0.5 : 0;
      for (let x = -cw; x < w + cw; x += cw) {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const ang = (Math.PI / 3) * i + Math.PI / 6;
          const px = x + xOff + Math.cos(ang) * r;
          const py = y + Math.sin(ang) * r;
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }
  } else if (pattern === 'camo') {
    ctx.fillStyle = secondaryHex;
    let s = (primary ^ secondary) >>> 0;
    const rng = () => {
      s = (s + 0x6d2b79f5) >>> 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = 0; i < 8; i++) {
      ctx.beginPath();
      const cx = rng() * w;
      const cy = rng() * h;
      const r = 4 + rng() * 8;
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (pattern === 'flames') {
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, secondaryHex);
    grad.addColorStop(1, primaryHex);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  } else if (pattern === 'circuit') {
    ctx.strokeStyle = secondaryHex;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(2, h * 0.3);
    ctx.lineTo(w * 0.4, h * 0.3);
    ctx.lineTo(w * 0.4, h * 0.7);
    ctx.lineTo(w - 2, h * 0.7);
    ctx.moveTo(w * 0.6, 2);
    ctx.lineTo(w * 0.6, h * 0.5);
    ctx.lineTo(w * 0.85, h * 0.5);
    ctx.stroke();
  }
}
